import { useState, useEffect, useRef } from "react";
import { API_BASE_URL, DEFAULT_JURISDICTION } from "../../config";
import { Button } from "../../components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "../../components/ui/card";
import { Input } from "../../components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { Textarea } from "../../components/ui/textarea";
import {
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
} from "../../components/ui/tabs";
import { Checkbox } from "../../components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../../components/ui/dialog";
import { toast } from "sonner";
import {
  Loader2,
  Eye,
  Download,
  Share2,
  Clock,
  Printer,
  FileText,
  Shield,
  Briefcase,
  Users,
  Handshake,
  Building2,
  ScrollText,
  KeyRound,
  UserCheck,
  FileSignature,
  FileSearch,
  FilePlus2,
  Edit,
  Trash,
  Plus,
  UploadCloud,
  CheckCircle,
  X,
  Save,
} from "lucide-react";
import { marked } from "marked";
import { format } from "date-fns";
import { es } from "date-fns/locale";
import { useAuth } from "@/contexts/AuthContext";
import { useDocumentHistory } from "@/hooks/useDocumentHistory";
import { useApiClient } from "@/hooks/use-api-client";
import React from "react";
import type { DocumentHistory } from "@/services/documentHistoryService";
import { addDocument, updateDocument } from "@/services/documentHistoryService";
import { saveDraft } from "@/services/customDocumentDraftService";
import { friendUsageGuard } from "@/lib/services/friend-usage-guard";
import { UsageTracker } from "@/lib/services/usage-tracker";

marked.setOptions({ breaks: true });

// MIME type for DOCX files
const DOCX_MIME_TYPE =
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document";

// Document type definitions
const DOCUMENT_TYPES = {
  contrato_laboral: {
    name: "Contrato de Trabajo",
    description:
      "Contrato de trabajo individual con todas las cláusulas legales requeridas",
    requiresDocuments: true,
    requiresSignatures: true,
    documentFields: [
      {
        party: "empleador",
        label: "Documentos del Empleador",
        required: true,
        documents: [
          {
            name: "nit_cedula",
            label: "NIT o Cédula",
            required: true,
          },
        ],
      },
      {
        party: "trabajador",
        label: "Documentos del Trabajador",
        required: true,
        documents: [
          { name: "cedula", label: "Cédula de Ciudadanía", required: true },
        ],
      },
    ],
    parameters: [
      { name: "empleador", label: "Empleador", type: "text", required: true },
      { name: "trabajador", label: "Trabajador", type: "text", required: true },
      { name: "cargo", label: "Cargo", type: "text", required: true },
      { name: "salario", label: "Salario", type: "text", required: true },
      { name: "duracion", label: "Duración", type: "text", required: true },
      {
        name: "lugar_trabajo",
        label: "Lugar de Trabajo",
        type: "text",
        required: true,
      },
      {
        name: "fecha_inicio",
        label: "Fecha de Inicio",
        type: "date",
        required: true,
      },
      {
        name: "jornada_laboral",
        label: "Jornada Laboral",
        type: "select",
        options: ["Tiempo Completo", "Medio Tiempo", "Por Horas"],
        required: true,
      },
      {
        name: "tipo_contrato",
        label: "Tipo de Contrato",
        type: "select",
        options: ["Término Fijo", "Término Indefinido", "Obra o Labor"],
        required: true,
      },
    ],
  },
  contrato_prestacion_servicios: {
    name: "Contrato de Servicios",
    description: "Contrato de prestación de servicios profesionales",
    requiresDocuments: true,
    requiresSignatures: true,
    documentFields: [
      {
        party: "contratante",
        label: "Documentos del Contratante",
        required: true,
        documents: [
          {
            name: "nit_cedula",
            label: "NIT o Cédula",
            required: true,
          },
        ],
      },
      {
        party: "contratista",
        label: "Documentos del Contratista",
        required: true,
        documents: [
          { name: "cedula", label: "Cédula de Ciudadanía", required: true },
        ],
      },
    ],
    parameters: [
      {
        name: "contratante",
        label: "Contratante",
        type: "text",
        required: true,
      },
      {
        name: "contratista",
        label: "Contratista",
        type: "text",
        required: true,
      },
      {
        name: "objeto",
        label: "Objeto del Contrato",
        type: "textarea",
        required: true,
      },
      { name: "honorarios", label: "Honorarios", type: "text", required: true },
      { name: "duracion", label: "Duración", type: "text", required: true },
      {
        name: "lugar_ejecucion",
        label: "Lugar de Ejecución",
        type: "text",
        required: true,
      },
      {
        name: "fecha_inicio",
        label: "Fecha de Inicio",
        type: "date",
        required: true,
      },
      {
        name: "forma_pago",
        label: "Forma de Pago",
        type: "select",
        options: ["Mensual", "Quincenal", "Por Entregables", "Al Finalizar"],
        required: true,
      },
      {
        name: "terminos_entrega",
        label: "Términos de Entrega",
        type: "textarea",
        required: false,
      },
    ],
  },
  contrato_arrendamiento: {
    name: "Contrato de Arrendamiento",
    parameters: [
      { name: "arrendador", label: "Arrendador", type: "text", required: true },
      {
        name: "arrendatario",
        label: "Arrendatario",
        type: "text",
        required: true,
      },
      {
        name: "inmueble",
        label: "Descripción del Inmueble",
        type: "textarea",
        required: true,
      },
      {
        name: "valor_arriendo",
        label: "Valor del Arriendo",
        type: "text",
        required: true,
      },
      {
        name: "duracion",
        label: "Duración del Contrato",
        type: "text",
        required: true,
      },
      { name: "garantia", label: "Garantía", type: "text", required: true },
    ],
  },
  contrato_compraventa: {
    name: "Contrato de Compraventa",
    parameters: [
      { name: "vendedor", label: "Vendedor", type: "text", required: true },
      { name: "comprador", label: "Comprador", type: "text", required: true },
      {
        name: "bien",
        label: "Descripción del Bien",
        type: "textarea",
        required: true,
      },
      { name: "precio", label: "Precio", type: "text", required: true },
      {
        name: "forma_pago",
        label: "Forma de Pago",
        type: "text",
        required: true,
      },
      {
        name: "plazo_entrega",
        label: "Plazo de Entrega",
        type: "text",
        required: true,
      },
    ],
  },
  poder_general: {
    name: "Poder General",
    parameters: [
      { name: "poderdante", label: "Poderdante", type: "text", required: true },
      { name: "apoderado", label: "Apoderado", type: "text", required: true },
      {
        name: "objeto_poder",
        label: "Objeto del Poder",
        type: "textarea",
        required: true,
      },
      { name: "duracion", label: "Duración", type: "text", required: true },
    ],
  },
  poder_especial: {
    name: "Poder Especial",
    parameters: [
      { name: "poderdante", label: "Poderdante", type: "text", required: true },
      { name: "apoderado", label: "Apoderado", type: "text", required: true },
      {
        name: "acto_especifico",
        label: "Acto Específico",
        type: "textarea",
        required: true,
      },
      { name: "duracion", label: "Duración", type: "text", required: true },
    ],
  },
  derecho_peticion: {
    name: "Derecho de Petición",
    parameters: [
      {
        name: "peticionario",
        label: "Peticionario",
        type: "text",
        required: true,
      },
      { name: "autoridad", label: "Autoridad", type: "text", required: true },
      {
        name: "objeto_peticion",
        label: "Objeto de la Petición",
        type: "textarea",
        required: true,
      },
      { name: "hechos", label: "Hechos", type: "textarea", required: true },
    ],
  },
  acta_constitucion: {
    name: "Acta de Constitución",
    parameters: [
      {
        name: "nombre_sociedad",
        label: "Nombre de la Sociedad",
        type: "text",
        required: true,
      },
      {
        name: "tipo_sociedad",
        label: "Tipo de Sociedad",
        type: "text",
        required: true,
      },
      {
        name: "capital_social",
        label: "Capital Social",
        type: "text",
        required: true,
      },
      { name: "socios", label: "Socios", type: "textarea", required: true },
      {
        name: "objeto_social",
        label: "Objeto Social",
        type: "textarea",
        required: true,
      },
    ],
  },
  estatutos_sociedad: {
    name: "Estatutos de Sociedad",
    parameters: [
      {
        name: "nombre_sociedad",
        label: "Nombre de la Sociedad",
        type: "text",
        required: true,
      },
      {
        name: "tipo_sociedad",
        label: "Tipo de Sociedad",
        type: "text",
        required: true,
      },
      {
        name: "capital_social",
        label: "Capital Social",
        type: "text",
        required: true,
      },
      {
        name: "objeto_social",
        label: "Objeto Social",
        type: "textarea",
        required: true,
      },
      { name: "duracion", label: "Duración", type: "text", required: true },
      { name: "domicilio", label: "Domicilio", type: "text", required: true },
    ],
  },
  acuerdo_confidencialidad: {
    name: "Acuerdos Comerciales",
    parameters: [
      { name: "parte_1", label: "Primera Parte", type: "text", required: true },
      { name: "parte_2", label: "Segunda Parte", type: "text", required: true },
      {
        name: "objeto",
        label: "Objeto del Acuerdo",
        type: "textarea",
        required: true,
      },
      { name: "duracion", label: "Duración", type: "text", required: true },
      {
        name: "informacion_confidencial",
        label: "Información Confidencial",
        type: "textarea",
        required: true,
      },
    ],
  },
};

interface ApiError {
  status?: number;
  message?: string;
}

// Map document type keys to icons
const CATEGORY_ICONS: Record<string, React.ElementType> = {
  contrato_laboral: Briefcase,
  contrato_prestacion_servicios: Handshake,
  contrato_arrendamiento: Building2,
  contrato_compraventa: FileText,
  poder_general: KeyRound,
  poder_especial: KeyRound,
  derecho_peticion: ScrollText,
  acta_constitucion: FileSignature,
  estatutos_sociedad: FileSearch,
  acuerdo_confidencialidad: Shield,
};

const getCategoryIcon = (key: string) => CATEGORY_ICONS[key] || FilePlus2;

interface CustomDocumentFormProps {
  onSubmit: (formData: CustomDocumentFormData) => void;
  onSaveDraft: (formData: CustomDocumentFormData) => void;
}

export interface CustomDocumentFormData {
  documentType: string;
  description: string;
  keyPoints: string;
  industry: string;
  jurisdiction: string;
  complexity: string;
  parties: Array<{ name: string; identification: string; role: string }>;
  urgencyLevel: string;
  includeAttachments: boolean;
  language: string;
  format: string;
  legalFramework: string[];
}

function CustomDocumentForm({
  onSubmit,
  onSaveDraft,
}: CustomDocumentFormProps) {
  const [formData, setFormData] = useState({
    documentType: "",
    description: "",
    keyPoints: "",
    industry: "",
    jurisdiction: "",
    complexity: "",
    parties: [{ name: "", identification: "", role: "" }],
    urgencyLevel: "normal",
    includeAttachments: false,
    language: "spanish",
    format: "pdf",
    legalFramework: [],
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [touched, setTouched] = useState<Record<string, boolean>>({});

  const validateField = (name: string, value: unknown): string => {
    switch (name) {
      case "documentType":
        if (typeof value !== "string" || !value.trim())
          return "El tipo de documento es obligatorio";
        if (value.trim().length < 3)
          return "El tipo de documento debe tener al menos 3 caracteres";
        break;
      case "description":
        if (typeof value !== "string" || !value.trim())
          return "La descripción es obligatoria";
        if (value.trim().length < 20)
          return "La descripción debe tener al menos 20 caracteres";
        break;
      case "keyPoints":
        if (typeof value !== "string" || !value.trim())
          return "Los puntos clave son obligatorios";
        if (value.trim().length < 10)
          return "Los puntos clave deben tener al menos 10 caracteres";
        break;
      case "industry":
        if (!value) return "La industria es obligatoria";
        break;
      case "jurisdiction":
        if (!value) return "La jurisdicción es obligatoria";
        break;
      case "complexity":
        if (!value) return "La complejidad es obligatoria";
        break;
      case "parties":
        if (!Array.isArray(value) || value.length === 0) {
          return "Debe incluir al menos una parte";
        }
        for (let i = 0; i < value.length; i++) {
          const party = value[i];
          if (
            typeof party !== "object" ||
            !party ||
            !("name" in party) ||
            !("identification" in party) ||
            !("role" in party)
          ) {
            return `La parte ${i + 1} tiene un formato inválido`;
          }
          const typedParty = party as {
            name: string;
            identification: string;
            role: string;
          };
          if (!typedParty.name.trim()) {
            return `La parte ${i + 1} debe tener un nombre`;
          }
          if (!typedParty.identification.trim()) {
            return `La parte ${i + 1} debe tener identificación`;
          }
          if (!typedParty.role.trim()) {
            return `La parte ${i + 1} debe tener un rol`;
          }
        }
        break;
    }
    return "";
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    // Validate all fields
    Object.keys(formData).forEach((key) => {
      const error = validateField(key, formData[key as keyof typeof formData]);
      if (error) {
        newErrors[key] = error;
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const handleSelectChange = (name: string, value: string) => {
    setFormData((prev) => ({ ...prev, [name]: value }));

    // Clear error when user selects an option
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const handlePartyChange = (index: number, field: string, value: string) => {
    const updatedParties = [...formData.parties];
    updatedParties[index] = { ...updatedParties[index], [field]: value };
    setFormData((prev) => ({ ...prev, parties: updatedParties }));

    // Clear party errors when user starts typing
    const partyErrorKey = `parties`;
    if (errors[partyErrorKey]) {
      setErrors((prev) => ({ ...prev, [partyErrorKey]: "" }));
    }
  };

  const addParty = () => {
    setFormData((prev) => ({
      ...prev,
      parties: [...prev.parties, { name: "", identification: "", role: "" }],
    }));
  };

  const removeParty = (index: number) => {
    const updatedParties = [...formData.parties];
    updatedParties.splice(index, 1);
    setFormData((prev) => ({ ...prev, parties: updatedParties }));

    // Revalidate parties after removal
    const partyError = validateField("parties", updatedParties);
    if (partyError) {
      setErrors((prev) => ({ ...prev, parties: partyError }));
    } else {
      setErrors((prev) => ({ ...prev, parties: "" }));
    }
  };

  const handleBlur = (name: string) => {
    setTouched((prev) => ({ ...prev, [name]: true }));
    const error = validateField(name, formData[name as keyof typeof formData]);
    setErrors((prev) => ({ ...prev, [name]: error }));
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    // Mark all fields as touched
    const allTouched = Object.keys(formData).reduce((acc, key) => {
      acc[key] = true;
      return acc;
    }, {} as Record<string, boolean>);
    setTouched(allTouched);

    // Validate form
    if (!validateForm()) {
      toast.error("Por favor, corrija los errores en el formulario");
      return;
    }

    // Include selected legal frameworks in form data
    const submissionData = {
      ...formData,
      legalFramework: [],
    };

    onSubmit(submissionData);
    toast.success("Solicitud enviada", {
      description: "Su documento personalizado será redactado pronto.",
    });
  };

  const getFieldError = (name: string): string => {
    return touched[name] ? errors[name] || "" : "";
  };

  const isFieldInvalid = (name: string): boolean => {
    return touched[name] && !!errors[name];
  };

  return (
    <Card className="border-[#E5DEFF]">
      <CardHeader className="bg-[#F8F7FF] border-b border-[#E5DEFF]">
        <CardTitle className="text-xl text-[#6E59A5]">
          Crear documento personalizado
        </CardTitle>
        <CardDescription>
          Complete el formulario a continuación para solicitar la redacción de
          un documento legal personalizado adaptado a la legislación colombiana.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6 pt-6">
        <form onSubmit={handleSubmit}>
          <div className="space-y-6">
            <div className="space-y-2">
              <label
                htmlFor="document-type"
                className="text-sm font-medium text-[#1A1F2C]">
                Tipo de documento
              </label>
              <Input
                id="document-type"
                name="documentType"
                value={formData.documentType}
                onChange={handleInputChange}
                onBlur={() => handleBlur("documentType")}
                placeholder="Ej: Contrato de colaboración, Acuerdo de confidencialidad, Derecho de petición"
                className={`border-[#D6BCFA] focus:border-[#7E69AB] focus:ring-[#7E69AB] ${isFieldInvalid("documentType") ? "border-red-500" : ""
                  }`}
              />
              {isFieldInvalid("documentType") && (
                <p className="text-red-500 text-xs">
                  {getFieldError("documentType")}
                </p>
              )}
            </div>

            <div className="space-y-2">
              <div className="flex justify-between items-center">
                <label
                  htmlFor="document-description"
                  className="text-sm font-medium text-[#1A1F2C]">
                  Describa el documento que necesita
                </label>
                <span className="text-xs text-gray-500">
                  {formData.description.length}/2000 caracteres
                </span>
              </div>
              <Textarea
                id="document-description"
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                onBlur={() => handleBlur("description")}
                placeholder="Describa en detalle el tipo de documento legal que necesita redactar, incluyendo su objetivo, partes involucradas, términos importantes, etc."
                className={`min-h-[150px] border-[#D6BCFA] focus:border-[#7E69AB] focus:ring-[#7E69AB] ${isFieldInvalid("description") ? "border-red-500" : ""
                  }`}
                maxLength={2000}
              />
              {isFieldInvalid("description") && (
                <p className="text-red-500 text-xs">
                  {getFieldError("description")}
                </p>
              )}
            </div>

            {/* Parties section */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <label className="text-sm font-medium text-[#1A1F2C]">
                  Partes involucradas
                </label>
                <Button
                  type="button"
                  onClick={addParty}
                  variant="outline"
                  size="sm"
                  className="text-[#7E69AB] border-[#D6BCFA] hover:bg-[#F8F7FF] hover:text-[#6E59A5]">
                  <Plus size={16} className="mr-1" /> Agregar parte
                </Button>
              </div>

              {isFieldInvalid("parties") && (
                <p className="text-red-500 text-xs">
                  {getFieldError("parties")}
                </p>
              )}

              {formData.parties.map((party, index) => (
                <div
                  key={index}
                  className={`p-4 border rounded-md border-[#E5DEFF] bg-[#FAFAFF] ${isFieldInvalid("parties") ? "border-red-200" : ""
                    }`}>
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="font-medium text-[#6E59A5]">
                      Parte {index + 1}
                    </h4>
                    {index > 0 && (
                      <Button
                        type="button"
                        onClick={() => removeParty(index)}
                        variant="ghost"
                        size="sm"
                        className="h-8 w-8 p-0 text-[#8E9196]">
                        <Trash size={16} />
                      </Button>
                    )}
                  </div>
                  <div className="grid md:grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <label className="text-sm font-medium text-[#1A1F2C]">
                        Nombre/Razón social
                      </label>
                      <Input
                        value={party.name}
                        onChange={(e) =>
                          handlePartyChange(index, "name", e.target.value)
                        }
                        onBlur={() => handleBlur("parties")}
                        className={`border-[#D6BCFA] ${isFieldInvalid("parties") ? "border-red-500" : ""
                          }`}
                      />
                    </div>
                    <div className="space-y-2">
                      <label className="text-sm font-medium text-[#1A1F2C]">
                        NIT/CC/CE
                      </label>
                      <Input
                        value={party.identification}
                        onChange={(e) =>
                          handlePartyChange(
                            index,
                            "identification",
                            e.target.value
                          )
                        }
                        onBlur={() => handleBlur("parties")}
                        className={`border-[#D6BCFA] ${isFieldInvalid("parties") ? "border-red-500" : ""
                          }`}
                      />
                    </div>
                    <div className="space-y-2">
                      <label className="text-sm font-medium text-[#1A1F2C]">
                        Rol
                      </label>
                      <Input
                        value={party.role}
                        placeholder="Ej: Contratante, Contratista, Acreedor"
                        onChange={(e) =>
                          handlePartyChange(index, "role", e.target.value)
                        }
                        onBlur={() => handleBlur("parties")}
                        className={`border-[#D6BCFA] ${isFieldInvalid("parties") ? "border-red-500" : ""
                          }`}
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="space-y-2">
              <div className="flex justify-between items-center">
                <label
                  htmlFor="key-points"
                  className="text-sm font-medium text-[#1A1F2C]">
                  Puntos clave a incluir
                </label>
                <span className="text-xs text-gray-500">
                  {formData.keyPoints.length}/1000 caracteres
                </span>
              </div>
              <Textarea
                id="key-points"
                name="keyPoints"
                value={formData.keyPoints}
                onChange={handleInputChange}
                onBlur={() => handleBlur("keyPoints")}
                placeholder="Liste los puntos clave que desea asegurarse de incluir en el documento (uno por línea)"
                className={`min-h-[100px] border-[#D6BCFA] focus:border-[#7E69AB] focus:ring-[#7E69AB] ${isFieldInvalid("keyPoints") ? "border-red-500" : ""
                  }`}
                maxLength={1000}
              />
              {isFieldInvalid("keyPoints") && (
                <p className="text-red-500 text-xs">
                  {getFieldError("keyPoints")}
                </p>
              )}
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <label
                  htmlFor="industry"
                  className="text-sm font-medium text-[#1A1F2C]">
                  Industria
                </label>
                <Select
                  name="industry"
                  value={formData.industry}
                  onValueChange={(value) =>
                    handleSelectChange("industry", value)
                  }>
                  <SelectTrigger
                    id="industry"
                    className={`border-[#D6BCFA] focus:border-[#7E69AB] focus:ring-[#7E69AB] ${isFieldInvalid("industry") ? "border-red-500" : ""
                      }`}>
                    <SelectValue placeholder="Seleccionar industria" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="technology">Tecnología</SelectItem>
                    <SelectItem value="healthcare">Salud</SelectItem>
                    <SelectItem value="finance">Finanzas</SelectItem>
                    <SelectItem value="real-estate">Bienes raíces</SelectItem>
                    <SelectItem value="retail">Comercio</SelectItem>
                    <SelectItem value="agriculture">Agricultura</SelectItem>
                    <SelectItem value="oil-gas">Petróleo y Gas</SelectItem>
                    <SelectItem value="education">Educación</SelectItem>
                    <SelectItem value="construction">Construcción</SelectItem>
                    <SelectItem value="other">Otra</SelectItem>
                  </SelectContent>
                </Select>
                {isFieldInvalid("industry") && (
                  <p className="text-red-500 text-xs">
                    {getFieldError("industry")}
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <label
                  htmlFor="jurisdiction-custom"
                  className="text-sm font-medium text-[#1A1F2C]">
                  Jurisdicción aplicable
                </label>
                <Select
                  name="jurisdiction"
                  value={formData.jurisdiction}
                  onValueChange={(value) =>
                    handleSelectChange("jurisdiction", value)
                  }>
                  <SelectTrigger
                    id="jurisdiction-custom"
                    className={`border-[#D6BCFA] focus:border-[#7E69AB] focus:ring-[#7E69AB] ${isFieldInvalid("jurisdiction") ? "border-red-500" : ""
                      }`}>
                    <SelectValue placeholder="Seleccionar jurisdicción" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="federal">Nacional</SelectItem>
                    <SelectItem value="bogota">Bogotá D.C.</SelectItem>
                    <SelectItem value="medellin">Medellín</SelectItem>
                    <SelectItem value="cali">Cali</SelectItem>
                    <SelectItem value="barranquilla">Barranquilla</SelectItem>
                    <SelectItem value="bucaramanga">Bucaramanga</SelectItem>
                    <SelectItem value="cartagena">Cartagena</SelectItem>
                    <SelectItem value="pereira">Pereira</SelectItem>
                    <SelectItem value="other">Otra</SelectItem>
                  </SelectContent>
                </Select>
                {isFieldInvalid("jurisdiction") && (
                  <p className="text-red-500 text-xs">
                    {getFieldError("jurisdiction")}
                  </p>
                )}
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <label
                  htmlFor="complexity"
                  className="text-sm font-medium text-[#1A1F2C]">
                  Complejidad del documento
                </label>
                <Select
                  name="complexity"
                  value={formData.complexity}
                  onValueChange={(value) =>
                    handleSelectChange("complexity", value)
                  }>
                  <SelectTrigger
                    id="complexity"
                    className={`border-[#D6BCFA] focus:border-[#7E69AB] focus:ring-[#7E69AB] ${isFieldInvalid("complexity") ? "border-red-500" : ""
                      }`}>
                    <SelectValue placeholder="Seleccionar complejidad" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="simple">
                      Simple - Términos básicos
                    </SelectItem>
                    <SelectItem value="standard">
                      Estándar - Nivel de detalle normal
                    </SelectItem>
                    <SelectItem value="complex">
                      Complejo - Altamente detallado
                    </SelectItem>
                  </SelectContent>
                </Select>
                {isFieldInvalid("complexity") && (
                  <p className="text-red-500 text-xs">
                    {getFieldError("complexity")}
                  </p>
                )}
              </div>
            </div>

            <div className="flex justify-end gap-4">
              <Button
                type="button"
                variant="outline"
                className="border-[#D6BCFA] text-[#7E69AB] hover:bg-[#F8F7FF] hover:text-[#6E59A5]">
                Cancelar
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => onSaveDraft(formData)}
                className="border-[#D6BCFA] text-[#7E69AB] hover:bg-[#F8F7FF] hover:text-[#6E59A5]">
                Guardar borrador
              </Button>
              <Button
                type="submit"
                className="gap-2 bg-[#7E69AB] hover:bg-[#6E59A5]">
                <Edit size={16} /> Redactar documento
              </Button>
            </div>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}

// Document Upload Component
function DocumentUploadSection({
  documentType,
  onDocumentsUploaded,
}: {
  documentType: string;
  onDocumentsUploaded: (documents: UploadedDocument[]) => void;
}) {
  const [uploading, setUploading] = useState(false);
  const [uploadedDocs, setUploadedDocs] = useState<UploadedDocument[]>([]);
  const [documentTexts, setDocumentTexts] = useState<Record<string, string>>(
    {}
  );

  const handleDocumentText = async (
    documentText: string,
    party: string,
    docType: string,
    docLabel: string
  ) => {
    const docId = `${party}_${docType}_${Date.now()}`;
    const newDoc: UploadedDocument = {
      id: docId,
      name: docLabel, // Use the document label instead of file name
      documentText,
      party,
      documentType: docType,
      uploadedAt: new Date(),
      status: "pending",
    };

    setUploading(true);
    try {
      // Simulate save process
      await new Promise((resolve) => setTimeout(resolve, 500));

      newDoc.status = "uploaded";
      const updatedDocs = [...uploadedDocs, newDoc];
      setUploadedDocs(updatedDocs);
      onDocumentsUploaded(updatedDocs);

      toast.success(`Documento ${docLabel} guardado exitosamente`);
    } catch (error) {
      newDoc.status = "error";
      toast.error(`Error al guardar ${docLabel}`);
    } finally {
      setUploading(false);
    }
  };

  const removeDocument = (docId: string) => {
    const updatedDocs = uploadedDocs.filter((doc) => doc.id !== docId);
    setUploadedDocs(updatedDocs);
    onDocumentsUploaded(updatedDocs);
  };

  const currentTemplate = DOCUMENT_TYPES[documentType];
  if (!currentTemplate?.documentFields) {
    return (
      <div className="text-center py-8 text-gray-500">
        No se requieren documentos para este tipo
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="text-center">
        <h3 className="text-xl font-semibold mb-2">
          Información de Documentos
        </h3>
        <p className="text-gray-600">
          Complete la información de los documentos necesarios para completar el
          proceso
        </p>
      </div>

      {currentTemplate.documentFields.map((field) => (
        <Card
          key={field.party}
          className="border-2 border-dashed border-gray-300">
          <CardHeader>
            <CardTitle className="text-lg">{field.label}</CardTitle>
            <CardDescription>
              Documentos requeridos para {field.party}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {field.documents.map((doc) => {
                const docKey = `${field.party}_${doc.name}`;
                const isUploaded = uploadedDocs.some((d) =>
                  d.id.includes(docKey)
                );

                return (
                  <div key={doc.name} className="p-4 border rounded-lg">
                    <div className="mb-3">
                      <div className="font-medium">{doc.label}</div>
                      <div className="text-sm text-gray-500">
                        {doc.required ? "Requerido" : "Opcional"}
                      </div>
                    </div>

                    {!isUploaded ? (
                      <div className="space-y-3">
                        <div className="space-y-2">
                          <div className="flex justify-between items-center">
                            <span className="text-sm font-medium text-gray-700">
                              Información del documento
                            </span>
                            <span className="text-xs text-gray-500">
                              {(documentTexts[docKey] || "").length}/1500
                              caracteres
                            </span>
                          </div>
                          <Textarea
                            placeholder={`Ingrese la información para ${doc.label.toLowerCase()}`}
                            value={documentTexts[docKey] || ""}
                            onChange={(e) => {
                              setDocumentTexts((prev) => ({
                                ...prev,
                                [docKey]: e.target.value,
                              }));
                            }}
                            className="min-h-[100px]"
                            maxLength={1500}
                          />
                        </div>
                        <div className="flex justify-end">
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            disabled={
                              uploading || !documentTexts[docKey]?.trim()
                            }
                            onClick={() => {
                              if (documentTexts[docKey]?.trim()) {
                                handleDocumentText(
                                  documentTexts[docKey],
                                  field.party,
                                  doc.name,
                                  doc.label
                                );
                              }
                            }}>
                            {uploading ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              <FileText className="h-4 w-4" />
                            )}
                            Guardar Información
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <div className="flex items-center justify-between p-3 bg-green-50 border border-green-200 rounded">
                        <div className="flex items-center gap-2">
                          <CheckCircle className="h-4 w-4 text-green-600" />
                          <span className="text-sm text-green-700">
                            Información guardada
                          </span>
                        </div>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setDocumentTexts((prev) => ({
                              ...prev,
                              [docKey]: "",
                            }));
                            const updatedDocs = uploadedDocs.filter(
                              (d) => !d.id.includes(docKey)
                            );
                            setUploadedDocs(updatedDocs);
                            onDocumentsUploaded(updatedDocs);
                          }}>
                          <Edit className="h-4 w-4" />
                        </Button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Show uploaded documents */}
            {uploadedDocs.filter((doc) => doc.party === field.party).length >
              0 && (
                <div className="mt-4">
                  <h4 className="font-medium mb-2">Documentos subidos:</h4>
                  <div className="space-y-2">
                    {uploadedDocs
                      .filter((doc) => doc.party === field.party)
                      .map((doc) => (
                        <div
                          key={doc.id}
                          className="flex items-center justify-between p-2 bg-gray-50 rounded">
                          <div className="flex items-center gap-2">
                            <FileText className="h-4 w-4 text-green-600" />
                            <div>
                              <div className="text-sm font-medium">
                                {doc.name}
                              </div>
                              <div className="text-xs text-gray-500">
                                {doc.party} - {doc.documentType}
                              </div>
                              <div className="text-xs text-gray-400 mt-1">
                                {doc.documentText?.substring(0, 50)}...
                              </div>
                            </div>
                            {doc.status === "uploaded" && (
                              <span className="text-xs text-green-600">
                                ✓ Guardado
                              </span>
                            )}
                          </div>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => removeDocument(doc.id)}>
                            <Trash className="h-4 w-4" />
                          </Button>
                        </div>
                      ))}
                  </div>
                </div>
              )}
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

// Signature Canvas Component
function SignatureCanvas({
  onSave,
  onCancel,
}: {
  onSave: (signatureData: string) => void;
  onCancel: () => void;
}) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasSignature, setHasSignature] = useState(false);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    // Set canvas size
    canvas.width = 400;
    canvas.height = 200;

    // Set drawing style
    ctx.strokeStyle = "#000";
    ctx.lineWidth = 2;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
  }, []);

  const startDrawing = (e: React.MouseEvent<HTMLCanvasElement>) => {
    setIsDrawing(true);
    const canvas = canvasRef.current;
    if (!canvas) return;

    const rect = canvas.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    ctx.beginPath();
    ctx.moveTo(x, y);
  };

  const draw = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (!isDrawing) return;

    const canvas = canvasRef.current;
    if (!canvas) return;

    const rect = canvas.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    ctx.lineTo(x, y);
    ctx.stroke();
    setHasSignature(true);
  };

  const stopDrawing = () => {
    setIsDrawing(false);
  };

  const clearCanvas = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setHasSignature(false);
  };

  const saveSignature = () => {
    const canvas = canvasRef.current;
    if (!canvas || !hasSignature) return;

    const signatureData = canvas.toDataURL("image/png");
    onSave(signatureData);
  };

  return (
    <div className="space-y-4">
      <div className="text-center">
        <h4 className="text-lg font-medium mb-2">Firma Digital</h4>
        <p className="text-sm text-gray-600">
          Dibuje su firma en el área de abajo
        </p>
      </div>

      <div className="border-2 border-dashed border-gray-300 rounded-lg p-4">
        <canvas
          ref={canvasRef}
          className="border border-gray-300 rounded cursor-crosshair mx-auto"
          onMouseDown={startDrawing}
          onMouseMove={draw}
          onMouseUp={stopDrawing}
          onMouseLeave={stopDrawing}
        />
      </div>

      <div className="flex justify-center gap-3">
        <Button
          type="button"
          variant="outline"
          onClick={clearCanvas}
          disabled={!hasSignature}>
          Limpiar
        </Button>
        <Button type="button" variant="outline" onClick={onCancel}>
          Cancelar
        </Button>
        <Button type="button" onClick={saveSignature} disabled={!hasSignature}>
          Guardar Firma
        </Button>
      </div>
    </div>
  );
}

// Digital Signature Component
function DigitalSignatureSection({
  documentType,
  parameters,
  onSignaturesComplete,
  emailInputs,
  setEmailInputs,
}: {
  documentType: string;
  parameters: Record<string, string>;
  onSignaturesComplete: (signatures: DigitalSignature[]) => void;
  emailInputs: Record<string, string>;
  setEmailInputs: React.Dispatch<React.SetStateAction<Record<string, string>>>;
}) {
  const [signatures, setSignatures] = useState<DigitalSignature[]>([]);
  const [currentSignature, setCurrentSignature] =
    useState<DigitalSignature | null>(null);
  const [showSignatureCanvas, setShowSignatureCanvas] = useState(false);
  const [signingFor, setSigningFor] = useState<string>("");

  const currentTemplate = DOCUMENT_TYPES[documentType];
  if (!currentTemplate?.requiresSignatures) {
    return (
      <div className="text-center py-8 text-gray-500">
        No se requieren firmas para este documento
      </div>
    );
  }

  // Get parties from parameters
  const getParties = () => {
    const parties: { name: string; role: string }[] = [];

    if (documentType === "contrato_laboral") {
      parties.push(
        { name: parameters.empleador || "Empleador", role: "Empleador" },
        { name: parameters.trabajador || "Trabajador", role: "Trabajador" }
      );
    } else if (documentType === "contrato_prestacion_servicios") {
      parties.push(
        { name: parameters.contratante || "Contratante", role: "Contratante" },
        { name: parameters.contratista || "Contratista", role: "Contratista" }
      );
    }

    return parties;
  };

  const addSignature = (party: string, name: string, email: string) => {
    const newSignature: DigitalSignature = {
      id: `${party}_${Date.now()}`,
      party,
      name,
      email,
      status: "pending",
    };
    setSignatures([...signatures, newSignature]);
  };

  const openSignatureCanvas = (party: string) => {
    setSigningFor(party);
    setShowSignatureCanvas(true);
  };

  const handleSignatureSave = (signatureData: string) => {
    // handleSignatureSave called with signature data

    const partySignature = signatures.find((sig) => sig.party === signingFor);
    if (partySignature) {
      const updatedSignatures = signatures.map((sig) =>
        sig.party === signingFor
          ? {
            ...sig,
            status: "signed" as const,
            signedAt: new Date(),
            signatureData: signatureData,
          }
          : sig
      );
      // Signatures updated successfully
      setSignatures(updatedSignatures);
      onSignaturesComplete(updatedSignatures);
    } else {
      console.error("No signature found for party");
    }
    setShowSignatureCanvas(false);
    setSigningFor("");
  };

  return (
    <div className="space-y-6">
      <div className="text-center">
        <h3 className="text-xl font-semibold mb-2">Firmas Digitales</h3>
        <p className="text-gray-600">
          Configure las firmas digitales para las partes involucradas
        </p>
      </div>

      {getParties().map((party) => (
        <Card
          key={party.role}
          className="border-2 border-dashed border-gray-300">
          <CardHeader>
            <CardTitle className="text-lg">
              {party.role}: {party.name}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium">Nombre completo</label>
                  <Input
                    placeholder="Nombre completo"
                    value={party.name}
                    readOnly
                  />
                </div>
                <div>
                  <label className="text-sm font-medium">Email</label>
                  <Input
                    placeholder="email@ejemplo.com"
                    type="email"
                    value={emailInputs[party.role] || ""}
                    onChange={(e) =>
                      setEmailInputs((prev) => ({
                        ...prev,
                        [party.role]: e.target.value,
                      }))
                    }
                  />
                </div>
              </div>

              <div className="flex items-center gap-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    const email = emailInputs[party.role] || "";
                    if (email) {
                      addSignature(party.role, party.name, email);
                    }
                  }}
                  disabled={!emailInputs[party.role]?.trim()}>
                  <UserCheck className="h-4 w-4 mr-2" />
                  Agregar Firma
                </Button>

                {signatures.find((sig) => sig.party === party.role) && (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => openSignatureCanvas(party.role)}
                    disabled={
                      signatures.find((sig) => sig.party === party.role)
                        ?.status === "signed"
                    }>
                    <FileSignature className="h-4 w-4 mr-2" />
                    Firmar Documento
                  </Button>
                )}

                {signatures.find((sig) => sig.party === party.role)?.status ===
                  "signed" && (
                    <div className="flex items-center gap-2 text-green-600">
                      <CheckCircle className="h-4 w-4" />
                      <span className="text-sm">Firmado</span>
                    </div>
                  )}
              </div>
            </div>
          </CardContent>
        </Card>
      ))}

      {/* Signature Status */}
      {signatures.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Estado de Firmas</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {signatures.map((sig) => (
                <div
                  key={sig.id}
                  className="flex items-center justify-between p-2 border rounded">
                  <div>
                    <div className="font-medium">{sig.name}</div>
                    <div className="text-sm text-gray-500">{sig.email}</div>
                  </div>
                  <div className="flex items-center gap-2">
                    {sig.status === "pending" && (
                      <span className="text-yellow-600 text-sm">Pendiente</span>
                    )}
                    {sig.status === "signed" && (
                      <span className="text-green-600 text-sm">Firmado</span>
                    )}
                    {sig.status === "declined" && (
                      <span className="text-red-600 text-sm">Rechazado</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Signature Canvas Modal */}
      {showSignatureCanvas && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white rounded-lg shadow-xl p-6 max-w-2xl w-full mx-4">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">
                Firma Digital para {signingFor}
              </h3>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowSignatureCanvas(false)}>
                ✕
              </Button>
            </div>
            <SignatureCanvas
              onSave={handleSignatureSave}
              onCancel={() => setShowSignatureCanvas(false)}
            />
          </div>
        </div>
      )}
    </div>
  );
}

// 1. PreviewPlaceholder component
function PreviewPlaceholder() {
  return (
    <div className="border border-dashed rounded-lg p-8 flex flex-col items-center justify-center text-center bg-white my-8">
      <FileText className="h-12 w-12 text-gray-400 mb-4" />
      <div className="font-semibold text-lg mb-2">
        El documento se generará aquí
      </div>
      <div className="text-gray-500">
        Complete el formulario anterior y presione "Redactar documento" para
        crear un borrador personalizado.
      </div>
    </div>
  );
}

// Helper to strip markdown
function stripMarkdown(md: string): string {
  return md
    .replace(/\*\*(.*?)\*\*/g, "$1") // bold
    .replace(/\*(.*?)\*/g, "$1") // italic
    .replace(/__([^_]+)__/g, "$1") // bold underline
    .replace(/_([^_]+)_/g, "$1") // underline
    .replace(/`([^`]+)`/g, "$1") // inline code
    .replace(/^#+\s*(.*)/gm, "$1") // headers
    .replace(/\[(.*?)\]\(.*?\)/g, "$1") // links
    .replace(/!\[(.*?)\]\(.*?\)/g, "$1") // images
    .replace(/^>\s?/gm, "") // blockquotes (only at line start)
    .replace(/^-\s/gm, ""); // lists (only at line start)
  // do NOT trim or collapse newlines
}

// View Document Modal Component
function ViewDocumentModal({
  document,
  isOpen,
  onClose,
  onEdit,
  onDownload,
  onShare,
}: {
  document: DocumentHistory | null;
  isOpen: boolean;
  onClose: () => void;
  onEdit: (doc: DocumentHistory) => void;
  onDownload: (doc: DocumentHistory) => void;
  onShare: (doc: DocumentHistory) => void;
}) {
  if (!document) return null;

  const handlePrint = () => {
    const printWindow = window.open("", "_blank");
    if (!printWindow) return;
    printWindow.document.write(`
      <html>
        <head>
          <title>${document.title}</title>
          <style>
            body { font-family: 'Inter', Arial, sans-serif; margin: 40px; }
            .doc-preview-content { font-size: 1.1rem; line-height: 1.7; }
            .doc-preview-content pre, .doc-preview-content code {
              font-family: 'Fira Mono', 'Consolas', 'Menlo', monospace;
              background: #f3f4f6;
              border-radius: 6px;
              padding: 0.5em 1em;
              white-space: pre-wrap;
              word-break: break-word;
              font-size: 1em;
              overflow-x: auto;
            }
          </style>
        </head>
        <body>
          <div class="doc-preview-content">${marked(document.content)}</div>
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-6xl max-h-[90vh] overflow-hidden">
        <DialogHeader>
          <div className="flex items-center justify-between">
            <div>
              <DialogTitle className="text-xl font-bold">
                {document.title}
              </DialogTitle>
              <DialogDescription className="flex items-center gap-4 mt-2">
                <span className="flex items-center gap-1 text-sm text-gray-500">
                  <Clock className="h-4 w-4" />
                  {format(new Date(document.date), "dd/MMM/yyyy", {
                    locale: es,
                  })}
                </span>
                <span className="text-sm text-gray-500">{document.type}</span>
                <span
                  className={`px-2 py-1 rounded-full text-xs font-semibold ${document.status === "Firmado"
                    ? "bg-blue-100 text-blue-700"
                    : document.status === "Enviado"
                      ? "bg-gray-100 text-gray-700"
                      : "bg-gray-100 text-gray-700"
                    }`}>
                  {document.status}
                </span>
              </DialogDescription>
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => onEdit(document)}>
                <Edit className="h-4 w-4 mr-1" />
                Editar
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => onDownload(document)}>
                <Download className="h-4 w-4 mr-1" />
                Descargar
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => onShare(document)}>
                <Share2 className="h-4 w-4 mr-1" />
                {document.url ? "Compartir enlace" : "Compartir"}
              </Button>
              <Button variant="outline" size="sm" onClick={handlePrint}>
                <Printer className="h-4 w-4 mr-1" />
                Imprimir
              </Button>
            </div>
          </div>
        </DialogHeader>
        <div className="overflow-y-auto max-h-[calc(90vh-120px)]">
          <div
            className="prose max-w-none"
            style={{
              fontSize: "1.1rem",
              lineHeight: "1.7",
            }}
            dangerouslySetInnerHTML={{ __html: marked(document.content) as string }}
          />
        </div>
      </DialogContent>
    </Dialog>
  );
}

// Interface for uploaded documents
interface UploadedDocument {
  id: string;
  name: string;
  documentText: string;
  party: string;
  documentType: string;
  uploadedAt: Date;
  status: "pending" | "uploaded" | "error";
}

// Interface for digital signatures
interface DigitalSignature {
  id: string;
  party: string;
  name: string;
  email: string;
  signedAt?: Date;
  status: "pending" | "signed" | "declined";
  signatureData?: string;
}

export default function DocumentDraftingDashboard() {
  const { user } = useAuth();
  const apiClient = useApiClient();
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [documentType, setDocumentType] = useState("");
  const [jurisdiction, setJurisdiction] = useState(DEFAULT_JURISDICTION);
  const [draft, setDraft] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [parameters, setParameters] = useState<Record<string, string>>({});
  const [uploadedDocuments, setUploadedDocuments] = useState<
    UploadedDocument[]
  >([]);
  const [signatures, setSignatures] = useState<DigitalSignature[]>([]);
  const [currentStep, setCurrentStep] = useState<
    "parameters" | "documents" | "signatures" | "preview"
  >("parameters");
  const [validation, setValidation] = useState<{
    warnings: string[];
    missing_elements: string[];
  }>({ warnings: [], missing_elements: [] });
  const [missingFields, setMissingFields] = useState<string[]>([]);
  const [isEditing, setIsEditing] = useState(false);
  const [editedDraft, setEditedDraft] = useState<string | null>(null);
  const [editedParameters, setEditedParameters] = useState<
    Record<string, string>
  >({});
  const {
    documents: documentsHistory,
    loading: historyLoading,
    reload: reloadHistory,
    download,
    share,
    view,
    edit: editHistoryDoc,
    duplicate,
    remove: removeHistoryDoc,
  } = useDocumentHistory(user?.id || "");
  const [activeTab, setActiveTab] = useState("templates");

  // High-Fidelity V3 State
  const [hfTemplates, setHfTemplates] = useState<any[]>([]);
  const [selectedHfTemplate, setSelectedHfTemplate] = useState<string | null>(null);
  const [hfStructure, setHfStructure] = useState<{ sections: string[], variables: string[] }>({ sections: [], variables: [] });
  const [selectedSections, setSelectedSections] = useState<string[]>([]);
  const [hfLoading, setHfLoading] = useState(false);

  // Fetch High-Fidelity Templates on mount
  useEffect(() => {
    const fetchTemplates = async () => {
      try {
        const response = await apiClient.get("/api/v1/document-drafting/high-fidelity-templates");
        if (response && Array.isArray(response)) {
          setHfTemplates(response);
        }
      } catch (err) {
        console.error("Error fetching HF templates:", err);
      }
    };
    fetchTemplates();
  }, [apiClient]);

  // Handle HF Template selection
  const handleHfTemplateSelect = async (name: string) => {
    setSelectedHfTemplate(name);
    setDocumentType("hf_template"); // Switch to HF mode
    setHfLoading(true);
    try {
      const response = await apiClient.get(`/api/v1/document-drafting/template-structure/${encodeURIComponent(name)}`);
      if (response) {
        setHfStructure(response);
        setSelectedSections(response.sections || []);
        // Pre-fill parameters with variables if they exist
        const newParams = { ...parameters };
        response.variables?.forEach((v: string) => {
          if (!newParams[v]) newParams[v] = "";
        });
        setParameters(newParams);
      }
    } catch (err) {
      toast.error("Error al cargar la estructura de la plantilla");
    } finally {
      setHfLoading(false);
    }
  };
  const [pendingDelete, setPendingDelete] = useState<DocumentHistory | null>(
    null
  );
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState("recent");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [viewingDocument, setViewingDocument] =
    useState<DocumentHistory | null>(null);
  const [editingFromHistory, setEditingFromHistory] = useState(false);
  const [editingDocumentId, setEditingDocumentId] = useState<string | null>(
    null
  );
  const [emailInputs, setEmailInputs] = useState<Record<string, string>>({});

  // Filtered and sorted document types for main grid
  const filteredTypes = Object.entries(DOCUMENT_TYPES)
    .filter(([key, value]) => {
      const matchesSearch = value.name
        .toLowerCase()
        .includes(search.toLowerCase());
      const matchesCategory = !selectedCategory || key === selectedCategory;
      return matchesSearch && matchesCategory;
    })
    .sort((a, b) => {
      if (sort === "recent") return 0; // Default order
      // Add more sort logic if needed
      return a[1].name.localeCompare(b[1].name);
    });

  // Filtered categories for sidebar
  const filteredCategories = Object.entries(DOCUMENT_TYPES).filter(
    ([key, value]) =>
      value.name.toLowerCase().includes(categoryFilter.toLowerCase())
  );

  const handleParameterChange = (
    name: string,
    value: string,
    editing = false
  ) => {
    if (editing) {
      setEditedParameters((prev) => ({ ...prev, [name]: value }));
      setMissingFields((prev) => prev.filter((field) => field !== name));
    } else {
      setParameters((prev) => ({ ...prev, [name]: value }));
      setMissingFields((prev) => prev.filter((field) => field !== name));
    }
  };

  const handleEdit = () => {
    setIsEditing(true);
    setEditedDraft(stripMarkdown(draft || ""));
    setEditedParameters({ ...parameters });
  };

  const handleSaveEdit = async () => {
    setDraft(editedDraft);
    setParameters({ ...editedParameters });
    setIsEditing(false);

    // If editing from history, save changes to the database
    if (editingFromHistory && editingDocumentId && user?.id) {
      try {
        await updateDocument(editingDocumentId, {
          content: editedDraft || "",
          title: title || DOCUMENT_TYPES[documentType]?.name || documentType,
        });
        toast.success("Documento actualizado exitosamente");
        reloadHistory();
      } catch (error) {
        toast.error("Error al actualizar el documento");
        console.error("Error updating document:", error);
      }
    } else {
      toast.success("Cambios guardados");
    }

    // Reset editing state
    setEditingFromHistory(false);
    setEditingDocumentId(null);
  };

  const formatParties = (documentType: string) => {
    const partyMappings = {
      contrato_laboral: {
        party1: { field: "empleador", role: "Empleador" },
        party2: { field: "trabajador", role: "Trabajador" },
      },
      contrato_prestacion_servicios: {
        party1: { field: "contratante", role: "Contratante" },
        party2: { field: "contratista", role: "Contratista" },
      },
    };

    const mapping = partyMappings[documentType] || {
      party1: { field: "parte_1", role: "Parte 1" },
      party2: { field: "parte_2", role: "Parte 2" },
    };

    return [
      {
        name: parameters[mapping.party1.field] || "",
        role: mapping.party1.role,
      },
      {
        name: parameters[mapping.party2.field] || "",
        role: mapping.party2.role,
      },
    ];
  };

  const generateDraft = async () => {
    if (!documentType) {
      toast.error("Please select a document type");
      return;
    }

    // Validation logic for required parameters
    const requiredParams = selectedHfTemplate
      ? (hfStructure.variables || []).map((v: any) =>
          typeof v === "string" ? { name: v, label: v, required: true } : v
        )
      : DOCUMENT_TYPES[documentType]?.parameters || [];
    // Always require parties and IDs for templates
    const alwaysRequired = [
      { name: "parte_1", label: "Parte 1", required: true },
      { name: "cedula_parte_1", label: "Cédula Parte 1", required: true },
      { name: "parte_2", label: "Parte 2", required: true },
      { name: "cedula_parte_2", label: "Cédula Parte 2", required: true },
    ];

    // Automatically inject current date into parameters if missing
    const formattedFecha = new Date().toLocaleDateString('es-CO', { day: 'numeric', month: 'long', year: 'numeric' });
    if (!parameters.fecha_actual) {
      setParameters((prev: any) => ({ ...prev, fecha_actual: formattedFecha, fecha: prev.fecha || formattedFecha }));
    }

    const combinedRequired = [...alwaysRequired, ...requiredParams];

    const missing = combinedRequired
      .filter((param: any) => param.required && !parameters[param.name] && param.name !== "fecha_actual" && param.name !== "fecha")
      .map((param: any) => param.name);
    setMissingFields(missing);
    if (missing.length > 0) {
      toast.error(`Atención: Hay ${missing.length} campo(s) obligatorio(s) o de expediente sin diligenciar. Complételos para generar el documento sin marcas [INSERTAR...].`);
      return;
    }

    // Helper: build final draft by appending documents and signatures
    const buildFinalDraft = (baseContent: string): string => {
      try {
        // Documents section
        const documentsSection = uploadedDocuments.length
          ? [
            "\n\n## Documentos aportados\n",
            ...uploadedDocuments.map((doc) => {
              const header = `### ${doc.name} (${doc.party})`;
              const meta = `Tipo: ${doc.documentType} | Guardado: ${new Date(
                doc.uploadedAt
              ).toLocaleString()}`;
              const body = doc.documentText
                ? `\n${doc.documentText}\n`
                : "\n";
              return `${header}\n${meta}${body}`;
            }),
          ].join("")
          : "";

        // Signatures section to append at the end (summary) in two-column layout
        const roleTitle = (party: string): string => {
          const p = (party || "").toLowerCase();
          if (p.includes("empleador")) return "El Empleador";
          if (p.includes("trabajador")) return "El Trabajador";
          if (p.includes("contratante")) return "El Contratante";
          if (p.includes("contratista")) return "El Contratista";
          return party || "Parte";
        };

        const findIdForParty = (party: string): string => {
          const doc = uploadedDocuments.find(
            (d) =>
              d.party === party &&
              (d.documentType === "cedula" || d.documentType === "nit_cedula")
          );
          const text = doc?.documentText || "";
          const match = text.match(/(\d[\d.\-\s]{5,})/);
          if (match && match[1]) return match[1].trim();
          // Fallback to typed parameters
          const p = (party || "").toLowerCase();
          if (p.includes("empleador") || p.includes("contratante")) {
            return parameters["cedula_parte_1"] || "________________________";
          }
          if (p.includes("trabajador") || p.includes("contratista")) {
            return parameters["cedula_parte_2"] || "________________________";
          }
          // Generic fallback
          return "________________________";
        };

        const blockHtml = (
          sig: (typeof signatures)[number] | undefined,
          fallbackTitle: string
        ) => {
          if (!sig) {
            return `
              <div style="display:flex;flex-direction:column;gap:12px;">
                <div style="font-weight:700;font-size:18px;">${fallbackTitle}</div>
                <div style="height:110px"></div>
                <div style="border-top:2px solid #111;margin-top:8px;width:100%"></div>
                <div style="font-weight:700">&nbsp;</div>
                <div>C.C. ________________________</div>
              </div>`;
          }
          const title = roleTitle(sig.party);
          const idText = findIdForParty(sig.party);
          const line = `<div style="border-top:2px solid #111;margin-top:8px;width:100%"></div>`;
          const image = sig.signatureData
            ? `<img alt="Firma de ${sig.name}" src="${sig.signatureData}" style="max-height:110px;object-fit:contain;margin:8px 0;" />`
            : `<div style="height:110px"></div>`;
          return `
            <div style="display:flex;flex-direction:column;gap:12px;">
              <div style="font-weight:700;font-size:18px;">${title}</div>
              ${image}
              ${line}
              <div style="font-weight:700">${sig.name || ""}</div>
              <div>C.C. ${idText}</div>
            </div>`;
        };

        let leftSig: (typeof signatures)[number] | undefined;
        let rightSig: (typeof signatures)[number] | undefined;
        leftSig = signatures.find((s) =>
          /empleador|contratante/i.test(s.party)
        );
        rightSig = signatures.find((s) =>
          /trabajador|contratista/i.test(s.party)
        );
        if (!leftSig && signatures.length > 0) leftSig = signatures[0];
        if (!rightSig && signatures.length > 1) rightSig = signatures[1];

        const signaturesSection = (() => {
          // Prefer collected signatures; otherwise build from parameters
          if (signatures.length > 0) {
            return `\n\n## Firmas\n
            <div style="display:grid;grid-template-columns:1fr 1fr;gap:60px;margin-top:8px;align-items:flex-end;">
              ${blockHtml(leftSig, "El Empleador")}
              ${blockHtml(rightSig, "El Trabajador")}
            </div>
          `;
          }
          // Build simple block using provided party names and cedulas
          const leftName = parameters["parte_1"] || "";
          const rightName = parameters["parte_2"] || "";
          const leftId =
            parameters["cedula_parte_1"] || "________________________";
          const rightId =
            parameters["cedula_parte_2"] || "________________________";
          return `\n\n## Firmas\n
            <div style="display:grid;grid-template-columns:1fr 1fr;gap:60px;margin-top:8px;align-items:flex-end;">
              <div style="display:flex;flex-direction:column;gap:12px;">
                <div style="font-weight:700;font-size:18px;">El Empleador</div>
                <div style="height:110px"></div>
                <div style="border-top:2px solid #111;margin-top:8px;width:100%"></div>
                <div style="font-weight:700">${leftName}</div>
                <div>C.C. ${leftId}</div>
              </div>
              <div style="display:flex;flex-direction:column;gap:12px;">
                <div style="font-weight:700;font-size:18px;">El Trabajador</div>
                <div style="height:110px"></div>
                <div style="border-top:2px solid #111;margin-top:8px;width:100%"></div>
                <div style="font-weight:700">${rightName}</div>
                <div>C.C. ${rightId}</div>
              </div>
            </div>
          `;
        })();

        // Replace existing signature sections with proper signature blocks
        let baseWithInline = baseContent;

        // Find and replace signature placeholders with actual signatures
        // Handle multiple formats of signature placeholders
        const signaturePatterns = [
          /(Firma:\s*)(_{10,})/gi, // Firma: ___________
          /(Firma:\s*)(-{10,})/gi, // Firma: -----------
          /(Firma:\s*)(\s{10,})/gi, // Firma: [spaces]
          /(Firma:\s*)(\.{10,})/gi, // Firma: ...........
          /(Firma:\s*)(_{5,})/gi, // Firma: _____ (shorter)
          /(Firma:\s*)(-{5,})/gi, // Firma: ----- (shorter)
          /(Firma:\s*)(_+)/gi, // Firma: _ (any number of underscores)
          /(Firma:\s*)(-+)/gi, // Firma: - (any number of dashes)
        ];

        // Helper function to determine which signature to use based on context
        const getSignatureForContext = (contextText: string): string => {
          let signature = "__________________________";

          // Check for employer/contractor context
          if (
            contextText.includes("empleador") ||
            contextText.includes("contratante")
          ) {
            const leftSig = signatures.find((s) =>
              /empleador|contratante/i.test(s.party)
            );
            if (leftSig?.signatureData) {
              signature = `![Firma de ${leftSig.name}](${leftSig.signatureData})`;
            }
          }
          // Check for employee/contractor context
          else if (
            contextText.includes("trabajador") ||
            contextText.includes("contratista")
          ) {
            const rightSig = signatures.find((s) =>
              /trabajador|contratista/i.test(s.party)
            );
            if (rightSig?.signatureData) {
              signature = `![Firma de ${rightSig.name}](${rightSig.signatureData})`;
            }
          }

          return signature;
        };

        signaturePatterns.forEach((pattern) => {
          if (pattern.test(baseWithInline)) {
            baseWithInline = baseWithInline.replace(
              pattern,
              (match, prefix, placeholder) => {
                // Find the position of this match in the document
                const matchIndex = baseWithInline.indexOf(match);

                // Look for party names in the preceding lines to determine context
                const linesBefore = baseWithInline
                  .substring(0, matchIndex)
                  .split("\n")
                  .slice(-5);
                const contextText = linesBefore.join("\n").toLowerCase();

                const signature = getSignatureForContext(contextText);
                return `${prefix}${signature}`;
              }
            );
          }
        });

        // Also handle the signature section at the end of the document
        const signaturePattern =
          /(Firma de EL EMPLEADOR[\s\S]*?Fecha:.*?)(\n|$)/gi;
        if (signaturePattern.test(baseWithInline)) {
          // Replace the entire signature section with our new format
          const leftSig = signatures.find((s) =>
            /empleador|contratante/i.test(s.party)
          );
          const rightSig = signatures.find((s) =>
            /trabajador|contratista/i.test(s.party)
          );

          const newSignatureSection = `
Firma de EL EMPLEADOR

${leftSig?.signatureData
              ? `![Firma de ${leftSig.name}](${leftSig.signatureData})`
              : "__________________________"
            }

${leftSig?.name || parameters.empleador || "__________________________"}
Cédula: ${findIdForParty("empleador")}
Fecha: ${new Date().toLocaleDateString()}

Firma de EL TRABAJADOR

${rightSig?.signatureData
              ? `![Firma de ${rightSig.name}](${rightSig.signatureData})`
              : "__________________________"
            }

${rightSig?.name || parameters.trabajador || "__________________________"}
Cédula: ${findIdForParty("trabajador")}
Fecha: ${new Date().toLocaleDateString()}`;

          baseWithInline = baseWithInline.replace(
            signaturePattern,
            newSignatureSection
          );
        }

        // If we have signatures but they weren't injected by patterns, add them manually
        if (signatures.length > 0) {
          // Find the FIRMAS section and inject signatures there
          const firmasSectionPattern = /(## FIRMAS[\s\S]*?)(\n|$)/gi;
          if (firmasSectionPattern.test(baseWithInline)) {
            baseWithInline = baseWithInline.replace(
              firmasSectionPattern,
              (match, section, end) => {
                let updatedSection = section;

                // Replace signature placeholders in the FIRMAS section
                const leftSig = signatures.find((s) =>
                  /empleador|contratante/i.test(s.party)
                );
                const rightSig = signatures.find((s) =>
                  /trabajador|contratista/i.test(s.party)
                );

                // Replace employer signature
                if (leftSig?.signatureData) {
                  updatedSection = updatedSection.replace(
                    /(Firma:\s*)(_+)/gi,
                    `$1![Firma de ${leftSig.name}](${leftSig.signatureData})`
                  );
                }

                // Replace employee signature
                if (rightSig?.signatureData) {
                  updatedSection = updatedSection.replace(
                    /(Firma:\s*)(_+)/gi,
                    `$1![Firma de ${rightSig.name}](${rightSig.signatureData})`
                  );
                }

                return updatedSection + end;
              }
            );
          }

          // Also try to find "FIRMAS Y ACEPTACIÓN" section
          const firmasYAceptacionPattern =
            /(## FIRMAS Y ACEPTACIÓN[\s\S]*?)(\n|$)/gi;
          if (firmasYAceptacionPattern.test(baseWithInline)) {
            baseWithInline = baseWithInline.replace(
              firmasYAceptacionPattern,
              (match, section, end) => {
                let updatedSection = section;

                // Replace signature placeholders in the FIRMAS Y ACEPTACIÓN section
                const leftSig = signatures.find((s) =>
                  /empleador|contratante/i.test(s.party)
                );
                const rightSig = signatures.find((s) =>
                  /trabajador|contratista/i.test(s.party)
                );

                // Replace employer signature
                if (leftSig?.signatureData) {
                  updatedSection = updatedSection.replace(
                    /(Firma:\s*)(_+)/gi,
                    `$1![Firma de ${leftSig.name}](${leftSig.signatureData})`
                  );
                }

                // Replace employee signature
                if (rightSig?.signatureData) {
                  updatedSection = updatedSection.replace(
                    /(Firma:\s*)(_+)/gi,
                    `$1![Firma de ${rightSig.name}](${rightSig.signatureData})`
                  );
                }

                return updatedSection + end;
              }
            );
          }
        }

        // Final fallback: replace any remaining signature placeholders in the entire document
        if (signatures.length > 0) {
          const leftSig = signatures.find((s) =>
            /empleador|contratante/i.test(s.party)
          );
          const rightSig = signatures.find((s) =>
            /trabajador|contratista/i.test(s.party)
          );

          // Replace all signature placeholders with actual signatures
          baseWithInline = baseWithInline.replace(
            /(Firma:\s*)(_+)/gi,
            (match, prefix, placeholder) => {
              // Determine which signature to use based on the context
              const matchIndex = baseWithInline.indexOf(match);
              const linesBefore = baseWithInline
                .substring(0, matchIndex)
                .split("\n")
                .slice(-3);
              const contextText = linesBefore.join("\n").toLowerCase();

              if (
                contextText.includes("empleador") ||
                contextText.includes("contratante")
              ) {
                if (leftSig?.signatureData) {
                  return `${prefix}![Firma de ${leftSig.name}](${leftSig.signatureData})`;
                }
              } else if (
                contextText.includes("trabajador") ||
                contextText.includes("contratista")
              ) {
                if (rightSig?.signatureData) {
                  return `${prefix}![Firma de ${rightSig.name}](${rightSig.signatureData})`;
                }
              }

              return match; // Keep original if no signature found
            }
          );
        }

        return `${baseWithInline}${documentsSection}`;
      } catch (e) {
        console.error("Error composing final draft:", e);
        return baseContent;
      }
    };

    setLoading(true);
    setDraft(null);
    try {
      const requestBody = {
        document_type: selectedHfTemplate || documentType,
        jurisdiction,
        key_requirements: Object.entries(parameters).map(
          ([key, value]) => `${key}: ${value}`
        ),
        parties: formatParties(documentType),
        legal_terms: [],
        hf_metadata: selectedHfTemplate ? {
          template_id: selectedHfTemplate,
          sections: selectedSections
        } : undefined,
        data_processing: {
          type: "personal",
          consent: "explicit",
        },
      };

      // Use friendUsageGuard to enforce usage limits and track usage
      const result = await friendUsageGuard<
        Awaited<
          ReturnType<
            typeof apiClient.apiClient.draftDocumentEndpointApiV1DraftDocumentPost
          >
        >
      >({
        user: { id: user?.id || "", email: user?.email || "" },
        featureId: "document-generator",
        requestData: requestBody,
        callApi: () =>
          apiClient.apiClient.draftDocumentEndpointApiV1DraftDocumentPost(
            requestBody
          ),
        responseSummary: undefined,
        tokensUsed: undefined,
        costEstimate: undefined,
      });

      if (
        result.data.document &&
        typeof result.data.document.content === "string"
      ) {
        const finalContent = buildFinalDraft(result.data.document.content);
        setDraft(finalContent);
        setValidation({ warnings: [], missing_elements: [] });
        toast.success("Document draft generated successfully");
        // Track usage for dashboard statistics
        if (user?.id) {
          try {
            await UsageTracker.trackDocumentGenerator(
              user.id,
              "draft_generated",
              documentType
            );
          } catch (e) {
            console.error("Error tracking document generator usage:", e);
          }
        }
        if (user?.id) {
          try {
            await addDocument({
              user_id: user.id,
              title:
                title || DOCUMENT_TYPES[documentType]?.name || documentType,
              date: new Date().toISOString(),
              type: DOCUMENT_TYPES[documentType]?.name || documentType,
              status: "Borrador",
              content: finalContent,
              url: undefined,
            });
            reloadHistory();
          } catch (err) {
            toast.error(
              "Error saving document history: " + (err?.message || err)
            );
            console.error("Supabase insert error:", err);
          }
        }
        setParameters({});
        setTitle("");
        setDescription("");
        setMissingFields([]);
        setValidation({ warnings: [], missing_elements: [] });
      } else {
        throw new Error("No draft content received");
      }
    } catch (error: unknown) {
      const apiError = error as { status?: number; message?: string };
      if (apiError?.status === 429 || apiError?.status === 500) {
        toast.error(
          "Nuestro asistente de IA está experimentando un alto tráfico. Por favor, inténtelo de nuevo en breve."
        );
      } else if (
        error instanceof Error &&
        error.message === "Daily usage limit reached"
      ) {
        toast.error(
          "Has alcanzado el límite diario de uso para la prueba de amigo. Intenta de nuevo mañana."
        );
      } else {
        toast.error("Failed to generate document draft");
      }
      console.error("Error generating draft:", error);
    } finally {
      setLoading(false);
    }
  };

  const downloadDraft = () => {
    const contentToDownload = isEditing ? editedDraft : draft;
    if (!contentToDownload) return;
    // If there are signature images, prefer HTML export so they render
    const hasSignatureImages = (signatures || []).some(
      (s) => !!s.signatureData
    );

    const element = document.createElement("a");
    if (hasSignatureImages) {
      // For documents with signatures, process everything as markdown
      const htmlContent = marked.parse(contentToDownload) as string;
      const html = `<!DOCTYPE html><html><head><meta charset="utf-8"/><title>Documento</title><style>body{font-family:Arial,sans-serif;line-height:1.6;margin:40px;}img{max-width:100%;height:auto;max-height:80px;}</style></head><body>${htmlContent}</body></html>`;
      const file = new Blob([html], { type: "text/html" });
      element.href = URL.createObjectURL(file);
      element.download = `${documentType}_${new Date().toISOString()}.doc`;
    } else {
      const plainText = contentToDownload.replace(/[#*_`~>-]/g, "");
      const file = new Blob([plainText], { type: DOCX_MIME_TYPE });
      element.href = URL.createObjectURL(file);
      element.download = `${documentType}_${new Date().toISOString()}.docx`;
    }
    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);
  };

  const shareDocument = async () => {
    if (!draft) return;

    try {
      if (navigator.share) {
        await navigator.share({
          title: `Documento Legal - ${DOCUMENT_TYPES[documentType]?.name || documentType
            }`,
          text: draft,
          url: window.location.href,
        });
      } else {
        await navigator.clipboard.writeText(draft);
        toast.success("Documento copiado al portapapeles");
      }
    } catch (error) {
      console.error("Error sharing:", error);
      toast.error("Error al compartir documento");
    }
  };

  // Enhanced share function for documents from history
  const shareDocumentFromHistory = async (doc: DocumentHistory) => {
    try {
      if (navigator.share) {
        await navigator.share({
          title: `Documento Legal - ${doc.title}`,
          text: doc.content,
          url: doc.url || window.location.href,
        });
      } else {
        // If there's a URL from backend, copy that; otherwise copy content
        const shareText = doc.url ? doc.url : doc.content;
        await navigator.clipboard.writeText(shareText);
        toast.success(
          doc.url
            ? "Enlace del documento copiado al portapapeles"
            : "Documento copiado al portapapeles"
        );
      }
    } catch (error) {
      console.error("Error sharing document:", error);
      toast.error("Error al compartir documento");
    }
  };

  // Print handler
  const handlePrint = () => {
    const printWindow = window.open("", "_blank");
    if (!printWindow) return;
    printWindow.document.write(`
      <html>
        <head>
          <title>Vista previa del documento</title>
          <style>
            body { font-family: 'Inter', Arial, sans-serif; margin: 40px; }
            .doc-preview-content { font-size: 1.1rem; line-height: 1.7; }
            .doc-preview-content pre, .doc-preview-content code {
              font-family: 'Fira Mono', 'Consolas', 'Menlo', monospace;
              background: #f3f4f6;
              border-radius: 6px;
              padding: 0.5em 1em;
              white-space: pre-wrap;
              word-break: break-word;
              font-size: 1em;
              overflow-x: auto;
            }
          </style>
        </head>
        <body>
          <div class="doc-preview-content">${marked.parse(draft || "") as string}</div>
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
  };

  const handleCustomDocumentSubmit = async (
    formData: CustomDocumentFormData
  ) => {
    setLoading(true);
    setDraft(null);

    try {
      // Format parties for the API with identification field
      const formattedParties = formData.parties
        .filter((party) => party.name.trim() !== "")
        .map((party) => ({
          name: party.name,
          identification: party.identification || "N/A", // Add identification field
          role: party.role || "Parte",
        }));

      // Create the request body with the correct structure expected by the backend
      const requestBody = {
        document_type: "custom_document",
        description: formData.description, // Add description field
        key_points: formData.keyPoints, // Add key_points field
        industry: formData.industry, // Add industry field
        complexity: formData.complexity, // Add complexity field
        jurisdiction: formData.jurisdiction || DEFAULT_JURISDICTION,
        parties: formattedParties,
        legal_terms: formData.legalFramework,
        data_processing: {
          type: "personal",
          consent: "explicit",
        },
      };

      const result =
        await apiClient.apiClient.draftCustomDocumentEndpointApiV1DraftCustomDocumentPost(
          requestBody
        );

      if (
        result.data.document &&
        typeof result.data.document.content === "string"
      ) {
        setDraft(result.data.document.content);
        setValidation({ warnings: [], missing_elements: [] });

        // Save to document history
        if (user?.id) {
          try {
            await addDocument({
              user_id: user.id,
              title: formData.documentType || "Documento Personalizado",
              date: new Date().toISOString(),
              type: "Documento Personalizado",
              status: "Borrador",
              content: result.data.document.content,
              url: undefined,
            });
            reloadHistory();
          } catch (err) {
            toast.error(
              "Error saving document history: " + (err?.message || err)
            );
            console.error("Supabase insert error:", err);
          }
        }

        toast.success("Documento personalizado generado exitosamente");
        setActiveTab("templates"); // Switch to templates tab to show the preview
      } else {
        throw new Error("No draft content received");
      }
    } catch (error: unknown) {
      const apiError = error as ApiError;
      if (apiError?.status === 429 || apiError?.status === 500) {
        toast.error(
          "Nuestro asistente de IA está experimentando un alto tráfico. Por favor, inténtelo de nuevo en breve."
        );
      } else {
        toast.error("Error generando documento personalizado");
      }
      console.error("Error generating custom document:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveCustomDocumentDraft = async (
    formData: CustomDocumentFormData
  ) => {
    if (!user?.id) {
      toast.error("Debe iniciar sesión para guardar un borrador.");
      return;
    }

    if (!formData.documentType && !formData.description) {
      toast.error(
        "Por favor, agregue un tipo de documento o una descripción antes de guardar."
      );
      return;
    }

    try {
      await saveDraft(user.id, formData);
      toast.success("Borrador guardado exitosamente.");
    } catch (error) {
      const apiError = error as ApiError;
      toast.error("Error al guardar el borrador: " + apiError.message);
      console.error("Error saving draft:", error);
    }
  };

  // Enhanced view function
  const handleViewDocument = (doc: DocumentHistory) => {
    setViewingDocument(doc);
    setViewModalOpen(true);
  };

  // Enhanced edit function
  const handleEditDocument = (doc: DocumentHistory) => {
    // Extract document type from the document title or type
    const docType =
      Object.keys(DOCUMENT_TYPES).find(
        (key) =>
          DOCUMENT_TYPES[key].name === doc.type ||
          doc.title.toLowerCase().includes(key.toLowerCase())
      ) || "";

    // Set the document type and switch to templates tab
    setDocumentType(docType);
    setActiveTab("templates");

    // Set the draft content
    setDraft(doc.content);
    setEditedDraft(stripMarkdown(doc.content));

    // Try to extract parameters from the document content
    const extractedParams = extractParametersFromContent(doc.content, docType);
    setParameters(extractedParams);
    setEditedParameters(extractedParams);

    // Set editing state
    setIsEditing(true);
    setEditingFromHistory(true);
    setEditingDocumentId(doc.id);

    // Close view modal if open
    setViewModalOpen(false);

    toast.success("Documento cargado para edición");
  };

  // Function to extract parameters from document content
  const extractParametersFromContent = (
    content: string,
    docType: string
  ): Record<string, string> => {
    const params: Record<string, string> = {};

    if (!docType || !DOCUMENT_TYPES[docType]) return params;

    const template = DOCUMENT_TYPES[docType];
    const contentLower = content.toLowerCase();

    // Extract common parameters based on document type
    template.parameters.forEach((param) => {
      const paramName = param.name;
      const paramLabel = param.label.toLowerCase();

      // Look for patterns in the content
      const patterns = [
        new RegExp(`${paramLabel}[\\s:]*([^\\n]+)`, "i"),
        new RegExp(`${paramName}[\\s:]*([^\\n]+)`, "i"),
        new RegExp(`\\*\\*${paramLabel}\\*\\*[\\s:]*([^\\n]+)`, "i"),
      ];

      for (const pattern of patterns) {
        const match = content.match(pattern);
        if (match && match[1]) {
          params[paramName] = match[1].trim();
          break;
        }
      }
    });

    return params;
  };

  return (
    <Tabs
      value={activeTab}
      onValueChange={setActiveTab}
      className="max-w-7xl mx-auto py-8 px-2">
      <TabsList className="mb-6 w-full grid grid-cols-3">
        <TabsTrigger value="templates">
          <FileText className="mr-2 h-5 w-5" />
          Plantillas
        </TabsTrigger>
        <TabsTrigger value="custom">
          <FilePlus2 className="mr-2 h-5 w-5" />
          Documento personalizado
        </TabsTrigger>
        <TabsTrigger value="history">
          <Clock className="mr-2 h-5 w-5" />
          Historial
        </TabsTrigger>
      </TabsList>
      <TabsContent value="templates">
        {/* V3 Premium High-Fidelity Section */}
        <Card className="mb-8 border-[#D6BCFA] bg-[#F9F7FF] shadow-sm">
          <CardHeader>
            <CardTitle className="text-[#6E59A5] flex items-center gap-2">
              <Shield className="h-5 w-5" />
              V3 Premium: Minutas de Alta Fidelidad (Verbatim)
            </CardTitle>
            <CardDescription>
              Documentos con fidelidad legal absoluta, extraídos directamente de Notarías y Minutas Oficiales.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex flex-col md:flex-row gap-4">
              <div className="flex-1">
                <Select onValueChange={handleHfTemplateSelect}>
                  <SelectTrigger className="border-[#D6BCFA] bg-white">
                    <SelectValue placeholder="Seleccionar Minuta de Alta Fidelidad..." />
                  </SelectTrigger>
                  <SelectContent>
                    {hfTemplates.map((t) => (
                      <SelectItem key={t.name} value={t.name}>
                        {t.name} ({t.category})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {selectedHfTemplate ? (
                <Button
                  onClick={() => setCurrentStep("parameters")}
                  className="bg-[#7E69AB] hover:bg-[#6E59A5]"
                >
                  Comenzar Redacción Verbatim
                </Button>
              ) : (
                <Button
                  variant="ghost"
                  disabled
                  className="text-gray-400"
                >
                  Seleccione una minuta para continuar
                </Button>
              )}
            </div>

            {selectedHfTemplate && hfStructure.sections.length > 0 && (
              <div className="mt-6 p-4 border rounded-md bg-white">
                <h4 className="font-semibold text-[#6E59A5] mb-2 flex items-center gap-2">
                  <FileSearch className="h-4 w-4" />
                  Configuración de Cláusulas (V3 Clause Selection)
                </h4>
                <p className="text-xs text-gray-500 mb-4">Seleccione las secciones que desea incluir en el documento final.</p>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                  {hfStructure.sections.map((section) => (
                    <div key={section} className="flex items-center space-x-2">
                      <Checkbox
                        id={`section-${section}`}
                        checked={selectedSections.includes(section)}
                        onCheckedChange={(checked) => {
                          if (checked) {
                            setSelectedSections([...selectedSections, section]);
                          } else {
                            setSelectedSections(selectedSections.filter(s => s !== section));
                          }
                        }}
                      />
                      <label
                        htmlFor={`section-${section}`}
                        className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 capitalize"
                      >
                        {section.replace(/_/g, " ")}
                      </label>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        <div className="flex flex-col md:flex-row gap-6">
          {/* Sidebar: Categories */}
          <aside className="w-full md:w-64 mb-4 md:mb-0 md:mr-6">
            <Card className="sticky top-8">
              <CardHeader>
                <CardTitle className="text-lg">
                  Categorías de documentos
                </CardTitle>
              </CardHeader>
              <CardContent className="flex flex-col gap-2">
                <Input
                  placeholder="Filtrar categorías..."
                  value={categoryFilter}
                  onChange={(e) => setCategoryFilter(e.target.value)}
                  className="mb-2"
                />
                <Button
                  variant={selectedCategory === null ? "default" : "ghost"}
                  className="flex items-center gap-2 justify-start w-full text-left"
                  onClick={() => setSelectedCategory(null)}>
                  <FileText className="h-5 w-5" />
                  <span>Todos los documentos</span>
                </Button>
                {filteredCategories.map(([key, value]) => {
                  const Icon = getCategoryIcon(key);
                  return (
                    <Button
                      key={key}
                      variant={selectedCategory === key ? "default" : "ghost"}
                      className="flex items-center gap-2 justify-start w-full text-left"
                      onClick={() => setSelectedCategory(key)}>
                      <Icon className="h-5 w-5" />
                      <span>{value.name}</span>
                    </Button>
                  );
                })}
              </CardContent>
            </Card>
          </aside>
          {/* Main Content */}
          <main className="flex-1">
            {/* Header */}
            <div className="mb-6">
              <h1 className="text-3xl font-bold tracking-tight mb-1">
                Redacción de Documentos Legales
              </h1>
              <p className="text-muted-foreground mb-4">
                Generación automatizada de contratos y documentos legales
                adaptados a la legislación colombiana
              </p>
            </div>
            {/* Search & Filter */}
            <div className="flex flex-col md:flex-row gap-2 mb-6 items-center">
              <Input
                placeholder="Buscar plantillas..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="md:w-1/2"
              />
              <Select value={sort} onValueChange={setSort}>
                <SelectTrigger className="w-40">
                  <SelectValue>
                    {sort === "recent" ? "Más reciente" : "A-Z"}
                  </SelectValue>
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="recent">Más reciente</SelectItem>
                  <SelectItem value="az">A-Z</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {/* Templates Grid */}
            <div className="grid gap-6 grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 mb-10">
              {filteredTypes.map(([key, value]) => {
                const Icon = getCategoryIcon(key);
                return (
                  <Card key={key} className="hover:shadow-lg transition-shadow">
                    <CardHeader className="flex flex-row items-center gap-3 pb-2">
                      <Icon className="h-6 w-6 text-legal-blue" />
                      <CardTitle className="text-lg font-semibold">
                        {value.name}
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-sm text-gray-600 mb-4">
                        Plantilla para {value.name.toLowerCase()}.
                      </p>
                      <div className="flex items-center gap-4 mb-4">
                        <span className="flex items-center gap-1 text-xs text-gray-500">
                          <Clock className="h-4 w-4" /> 10-15 min
                        </span>
                        <span className="flex items-center gap-1 text-xs text-gray-500">
                          <Shield className="h-4 w-4" /> Complejidad: Media
                        </span>
                      </div>
                      <Button
                        variant="outline"
                        className="w-full"
                        onClick={() => setDocumentType(key)}>
                        Generar
                      </Button>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
            {/* Step Indicator */}
            {documentType && (
              <div className="mb-6">
                <div className="flex items-center justify-center space-x-4">
                  <div
                    className={`flex items-center space-x-2 ${currentStep === "parameters"
                      ? "text-blue-600"
                      : "text-gray-400"
                      }`}>
                    <div
                      className={`w-8 h-8 rounded-full flex items-center justify-center ${currentStep === "parameters"
                        ? "bg-blue-600 text-white"
                        : "bg-gray-200"
                        }`}>
                      1
                    </div>
                    <span className="text-sm font-medium">Parámetros</span>
                  </div>
                  <div
                    className={`w-8 h-0.5 ${currentStep === "documents" ||
                      currentStep === "signatures" ||
                      currentStep === "preview"
                      ? "bg-blue-600"
                      : "bg-gray-200"
                      }`}></div>
                  <div
                    className={`flex items-center space-x-2 ${currentStep === "documents"
                      ? "text-blue-600"
                      : currentStep === "signatures" ||
                        currentStep === "preview"
                        ? "text-blue-600"
                        : "text-gray-400"
                      }`}>
                    <div
                      className={`w-8 h-8 rounded-full flex items-center justify-center ${currentStep === "documents"
                        ? "bg-blue-600 text-white"
                        : currentStep === "signatures" ||
                          currentStep === "preview"
                          ? "bg-blue-600 text-white"
                          : "bg-gray-200"
                        }`}>
                      2
                    </div>
                    <span className="text-sm font-medium">Documentos</span>
                  </div>
                  <div
                    className={`w-8 h-0.5 ${currentStep === "signatures" || currentStep === "preview"
                      ? "bg-blue-600"
                      : "bg-gray-200"
                      }`}></div>
                  <div
                    className={`flex items-center space-x-2 ${currentStep === "signatures"
                      ? "text-blue-600"
                      : currentStep === "preview"
                        ? "text-blue-600"
                        : "text-gray-400"
                      }`}>
                    <div
                      className={`w-8 h-8 rounded-full flex items-center justify-center ${currentStep === "signatures"
                        ? "bg-blue-600 text-white"
                        : currentStep === "preview"
                          ? "bg-blue-600 text-white"
                          : "bg-gray-200"
                        }`}>
                      3
                    </div>
                    <span className="text-sm font-medium">Firmas</span>
                  </div>
                  <div
                    className={`w-8 h-0.5 ${currentStep === "preview" ? "bg-blue-600" : "bg-gray-200"
                      }`}></div>
                  <div
                    className={`flex items-center space-x-2 ${currentStep === "preview"
                      ? "text-blue-600"
                      : "text-gray-400"
                      }`}>
                    <div
                      className={`w-8 h-8 rounded-full flex items-center justify-center ${currentStep === "preview"
                        ? "bg-blue-600 text-white"
                        : "bg-gray-200"
                        }`}>
                      4
                    </div>
                    <span className="text-sm font-medium">Vista Previa</span>
                  </div>
                </div>
              </div>
            )}

            {/* Parameter Form & Preview */}
            {documentType && currentStep === "parameters" && (
              <Card className="mb-8 shadow-md border-[#D6BCFA]">
                <CardHeader className="bg-[#FAF9FF] border-b border-[#E5DEFF] mb-6">
                  <CardTitle className="text-xl font-bold flex items-center gap-2 text-[#6E59A5]">
                    {selectedHfTemplate ? <Shield className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                    {selectedHfTemplate ? `Parámetros de Alta Fidelidad: ${selectedHfTemplate}` : "Parámetros del Documento"}
                  </CardTitle>
                  <CardDescription>
                    {selectedHfTemplate
                      ? "Complete las variables para el ensamble verbatim del documento."
                      : DOCUMENT_TYPES[documentType]?.description}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {(selectedHfTemplate ? hfStructure.variables.map(v => ({ name: v, label: v, required: true })) : (DOCUMENT_TYPES[documentType]?.parameters || [])).map((param) => (
                      <div key={param.name}>
                        <label className="text-sm font-medium mb-2 block">
                          {param.label}
                          {param.required && (
                            <span className="text-red-500 ml-1">*</span>
                          )}
                        </label>

                        {param.type === "textarea" ? (
                          <div className="space-y-2">
                            <div className="flex justify-between items-center">
                              <span className="text-sm font-medium text-gray-700">
                                {param.label}
                              </span>
                              <span className="text-xs text-gray-500">
                                {
                                  (isEditing
                                    ? editedParameters[param.name] || ""
                                    : parameters[param.name] || ""
                                  ).length
                                }
                                /1000 caracteres
                              </span>
                            </div>
                            <Textarea
                              placeholder={param.label}
                              value={
                                isEditing
                                  ? editedParameters[param.name] || ""
                                  : parameters[param.name] || ""
                              }
                              onChange={(e) =>
                                handleParameterChange(
                                  param.name,
                                  e.target.value,
                                  isEditing
                                )
                              }
                              className={`min-h-[100px]${missingFields.includes(param.name)
                                ? " border-red-500"
                                : ""
                                }`}
                              disabled={isEditing}
                              maxLength={1000}
                            />
                          </div>
                        ) : param.type === "date" ? (
                          <Input
                            type="date"
                            value={
                              isEditing
                                ? editedParameters[param.name] || ""
                                : parameters[param.name] || ""
                            }
                            onChange={(e) =>
                              handleParameterChange(
                                param.name,
                                e.target.value,
                                isEditing
                              )
                            }
                            className={
                              missingFields.includes(param.name)
                                ? "border-red-500"
                                : ""
                            }
                            disabled={isEditing}
                          />
                        ) : param.type === "select" ? (
                          <Select
                            value={
                              isEditing
                                ? editedParameters[param.name] || ""
                                : parameters[param.name] || ""
                            }
                            onValueChange={(value) =>
                              handleParameterChange(
                                param.name,
                                value,
                                isEditing
                              )
                            }
                            disabled={isEditing}>
                            <SelectTrigger
                              className={
                                missingFields.includes(param.name)
                                  ? "border-red-500"
                                  : ""
                              }>
                              <SelectValue
                                placeholder={`Seleccionar ${param.label.toLowerCase()}`}
                              />
                            </SelectTrigger>
                            <SelectContent>
                              {param.options?.map((option) => (
                                <SelectItem key={option} value={option}>
                                  {option}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        ) : (
                          <Input
                            placeholder={param.label}
                            value={
                              isEditing
                                ? editedParameters[param.name] || ""
                                : parameters[param.name] || ""
                            }
                            onChange={(e) =>
                              handleParameterChange(
                                param.name,
                                e.target.value,
                                isEditing
                              )
                            }
                            className={
                              missingFields.includes(param.name)
                                ? "border-red-500"
                                : ""
                            }
                            disabled={isEditing}
                          />
                        )}
                      </div>
                    ))}

                    {/* Always require Parties and Cédulas */}
                    <div className="grid md:grid-cols-2 gap-4 pt-2">
                      <div>
                        <label className="text-sm font-medium mb-2 block">
                          Parte 1 - Nombre completo{" "}
                          <span className="text-red-500">*</span>
                        </label>
                        <Input
                          placeholder="Nombre Parte 1"
                          value={
                            isEditing
                              ? editedParameters["parte_1"] || ""
                              : parameters["parte_1"] || ""
                          }
                          onChange={(e) =>
                            handleParameterChange(
                              "parte_1",
                              e.target.value,
                              isEditing
                            )
                          }
                          className={
                            missingFields.includes("parte_1")
                              ? "border-red-500"
                              : ""
                          }
                          disabled={isEditing}
                        />
                      </div>
                      <div>
                        <label className="text-sm font-medium mb-2 block">
                          Parte 1 - Cédula/NIT{" "}
                          <span className="text-red-500">*</span>
                        </label>
                        <Input
                          placeholder="Cédula Parte 1"
                          value={
                            isEditing
                              ? editedParameters["cedula_parte_1"] || ""
                              : parameters["cedula_parte_1"] || ""
                          }
                          onChange={(e) =>
                            handleParameterChange(
                              "cedula_parte_1",
                              e.target.value,
                              isEditing
                            )
                          }
                          className={
                            missingFields.includes("cedula_parte_1")
                              ? "border-red-500"
                              : ""
                          }
                          disabled={isEditing}
                        />
                      </div>
                      <div>
                        <label className="text-sm font-medium mb-2 block">
                          Parte 2 - Nombre completo{" "}
                          <span className="text-red-500">*</span>
                        </label>
                        <Input
                          placeholder="Nombre Parte 2"
                          value={
                            isEditing
                              ? editedParameters["parte_2"] || ""
                              : parameters["parte_2"] || ""
                          }
                          onChange={(e) =>
                            handleParameterChange(
                              "parte_2",
                              e.target.value,
                              isEditing
                            )
                          }
                          className={
                            missingFields.includes("parte_2")
                              ? "border-red-500"
                              : ""
                          }
                          disabled={isEditing}
                        />
                      </div>
                      <div>
                        <label className="text-sm font-medium mb-2 block">
                          Parte 2 - Cédula/NIT{" "}
                          <span className="text-red-500">*</span>
                        </label>
                        <Input
                          placeholder="Cédula Parte 2"
                          value={
                            isEditing
                              ? editedParameters["cedula_parte_2"] || ""
                              : parameters["cedula_parte_2"] || ""
                          }
                          onChange={(e) =>
                            handleParameterChange(
                              "cedula_parte_2",
                              e.target.value,
                              isEditing
                            )
                          }
                          className={
                            missingFields.includes("cedula_parte_2")
                              ? "border-red-500"
                              : ""
                          }
                          disabled={isEditing}
                        />
                      </div>
                    </div>

                    {!isEditing && (
                      <div className="flex gap-4 pt-4">
                        <Button
                          onClick={() => setCurrentStep("documents")}
                          disabled={!documentType || loading}
                          className="flex-1">
                          Siguiente: Documentos
                        </Button>
                        <Button
                          onClick={generateDraft}
                          disabled={!documentType || loading}
                          variant="outline">
                          {loading ? (
                            <>
                              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                              Generando...
                            </>
                          ) : (
                            "Generar Borrador"
                          )}
                        </Button>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Document Upload Section */}
            {documentType && currentStep === "documents" && (
              <Card className="mb-8 shadow-md">
                <CardHeader>
                  <CardTitle className="text-xl font-bold flex items-center gap-2">
                    <UploadCloud className="h-5 w-5" />
                    Documentos Requeridos
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <DocumentUploadSection
                    documentType={documentType}
                    onDocumentsUploaded={setUploadedDocuments}
                  />
                  <div className="flex gap-4 pt-6">
                    <Button
                      onClick={() => setCurrentStep("parameters")}
                      variant="outline">
                      Anterior: Parámetros
                    </Button>
                    <Button
                      onClick={() => setCurrentStep("signatures")}
                      className="flex-1">
                      Siguiente: Firmas
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Digital Signature Section */}
            {documentType && currentStep === "signatures" && (
              <Card className="mb-8 shadow-md">
                <CardHeader>
                  <CardTitle className="text-xl font-bold flex items-center gap-2">
                    <UserCheck className="h-5 w-5" />
                    Firmas Digitales
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <DigitalSignatureSection
                    documentType={documentType}
                    parameters={parameters}
                    onSignaturesComplete={setSignatures}
                    emailInputs={emailInputs}
                    setEmailInputs={setEmailInputs}
                  />
                  <div className="flex gap-4 pt-6">
                    <Button
                      onClick={() => setCurrentStep("documents")}
                      variant="outline">
                      Anterior: Documentos
                    </Button>
                    <Button
                      onClick={() => setCurrentStep("preview")}
                      className="flex-1">
                      Siguiente: Vista Previa
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Preview and Generate Section */}
            {documentType && currentStep === "preview" && (
              <Card className="mb-8 shadow-md">
                <CardHeader>
                  <CardTitle className="text-xl font-bold flex items-center gap-2">
                    <Eye className="h-5 w-5" />
                    Resumen y Generación
                  </CardTitle>
                  <CardDescription>
                    Revise los parámetros, documentos y firmas antes de generar
                    el documento
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {/* Parameters Summary */}
                  <div className="mb-6">
                    <h3 className="text-lg font-semibold mb-3">
                      Parámetros del Documento
                    </h3>
                    <div className="grid grid-cols-2 gap-4">
                      {Object.entries(parameters).map(([key, value]) => (
                        <div key={key} className="p-3 border rounded-lg">
                          <div className="text-sm font-medium text-gray-600">
                            {key}
                          </div>
                          <div className="text-sm">{value}</div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Documents Summary */}
                  {uploadedDocuments.length > 0 && (
                    <div className="mb-6">
                      <h3 className="text-lg font-semibold mb-3">
                        Documentos Subidos
                      </h3>
                      <div className="space-y-2">
                        {uploadedDocuments.map((doc) => (
                          <div
                            key={doc.id}
                            className="flex items-center justify-between p-3 border rounded-lg">
                            <div className="flex items-center gap-3">
                              <FileText className="h-5 w-5 text-green-600" />
                              <div>
                                <div className="font-medium">{doc.name}</div>
                                <div className="text-sm text-gray-500">
                                  {doc.party} - {doc.documentType}
                                </div>
                                <div className="text-xs text-gray-400 mt-1 max-w-md">
                                  {doc.documentText?.substring(0, 100)}...
                                </div>
                              </div>
                            </div>
                            <div className="text-sm text-green-600">
                              ✓ Guardado
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Signatures Summary */}
                  {signatures.length > 0 && (
                    <div className="mb-6">
                      <h3 className="text-lg font-semibold mb-3">
                        Estado de Firmas
                      </h3>
                      <div className="space-y-2">
                        {signatures.map((sig) => (
                          <div
                            key={sig.id}
                            className="flex items-center justify-between p-3 border rounded-lg">
                            <div>
                              <div className="font-medium">{sig.name}</div>
                              <div className="text-sm text-gray-500">
                                {sig.email}
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              {sig.status === "pending" && (
                                <span className="text-yellow-600 text-sm">
                                  Pendiente
                                </span>
                              )}
                              {sig.status === "signed" && (
                                <span className="text-green-600 text-sm">
                                  Firmado
                                </span>
                              )}
                              {sig.status === "declined" && (
                                <span className="text-red-600 text-sm">
                                  Rechazado
                                </span>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  <div className="flex gap-4 pt-6">
                    <Button
                      onClick={() => setCurrentStep("signatures")}
                      variant="outline">
                      Anterior: Firmas
                    </Button>
                    <Button
                      onClick={generateDraft}
                      disabled={loading}
                      className="flex-1">
                      {loading ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Generando Documento...
                        </>
                      ) : (
                        "Generar Documento Final"
                      )}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}
            {/* Preview Section */}
            {draft && (
              <Card className="bg-gray-50 border border-gray-200 shadow-sm mb-8">
                <CardHeader className="flex flex-row justify-between items-center pb-2">
                  <CardTitle className="text-xl font-bold flex items-center gap-2">
                    <Eye className="h-5 w-5" />
                    Vista previa
                  </CardTitle>
                  <div className="flex gap-2 flex-wrap">
                    {!isEditing && draft && (
                      <Button variant="outline" onClick={handleEdit}>
                        <Edit className="h-4 w-4 mr-1" />
                        Editar
                      </Button>
                    )}
                    {isEditing && (
                      <>
                        <Button variant="outline" onClick={handleSaveEdit}>
                          <Save className="h-4 w-4 mr-1" />
                          {editingFromHistory
                            ? "Guardar cambios"
                            : "Guardar cambios"}
                        </Button>
                        <Button
                          variant="outline"
                          onClick={() => {
                            setIsEditing(false);
                            setEditingFromHistory(false);
                            setEditingDocumentId(null);
                          }}>
                          <X className="h-4 w-4 mr-1" />
                          Cancelar
                        </Button>
                      </>
                    )}
                    {!isEditing && (
                      <Button
                        variant="outline"
                        onClick={shareDocument}
                        disabled={!draft}>
                        <Share2 className="h-4 w-4 mr-2" />
                        Compartir
                      </Button>
                    )}
                    {!isEditing && draft && (
                      <Button variant="outline" onClick={handlePrint}>
                        <Printer className="h-4 w-4 mr-2" />
                        Imprimir
                      </Button>
                    )}
                    {(!isEditing || draft) && (
                      <Button variant="outline" onClick={downloadDraft}>
                        <Download className="h-4 w-4 mr-2" />
                        Descargar
                      </Button>
                    )}
                  </div>
                </CardHeader>
                <CardContent>
                  {isEditing ? (
                    <div>
                      <div className="flex justify-between items-center mb-2">
                        <span className="text-sm font-medium text-gray-700">
                          Contenido del documento
                        </span>
                        <span className="text-xs text-gray-500">
                          {(editedDraft || "").length}/10000 caracteres
                        </span>
                      </div>
                      <Textarea
                        className="min-h-[300px] mb-4"
                        value={editedDraft || ""}
                        onChange={(e) => setEditedDraft(e.target.value)}
                        maxLength={10000}
                      />
                    </div>
                  ) : (
                    <>
                      <style>{`
                        .doc-preview-content {
                          line-height: 1.8;
                          font-size: 1.1rem;
                        }
                        .doc-preview-content p {
                          margin-bottom: 1em;
                        }
                        .doc-preview-content br {
                          display: block;
                          margin-bottom: 0.5em;
                          content: "";
                        }
                      `}</style>
                      <div
                        className="prose doc-preview-content"
                        style={{
                          width: "100%",
                          fontSize: "1.1rem",
                          lineHeight: "1.7",
                        }}
                        dangerouslySetInnerHTML={{ __html: marked(draft) as string }}
                      />
                    </>
                  )}
                </CardContent>
              </Card>
            )}
          </main>
        </div>
      </TabsContent>
      <TabsContent value="custom">
        <div className="max-w-4xl mx-auto">
          <CustomDocumentForm
            onSubmit={handleCustomDocumentSubmit}
            onSaveDraft={handleSaveCustomDocumentDraft}
          />
          {loading ? (
            <div className="text-center py-8">
              <Loader2 className="h-8 w-8 animate-spin mx-auto mb-2" />
              <p className="text-gray-500">Generando documento...</p>
            </div>
          ) : draft ? (
            <Card className="bg-gray-50 border border-gray-200 shadow-sm my-8">
              <CardHeader className="flex flex-row justify-between items-center pb-2">
                <CardTitle className="text-xl font-bold flex items-center gap-2">
                  <Eye className="h-5 w-5" />
                  Vista previa
                </CardTitle>
                <div className="flex gap-2 flex-wrap">
                  {!isEditing && draft && (
                    <Button variant="outline" onClick={handleEdit}>
                      Editar
                    </Button>
                  )}
                  {isEditing && (
                    <>
                      <Button variant="outline" onClick={handleSaveEdit}>
                        Guardar cambios
                      </Button>
                      <Button
                        variant="outline"
                        onClick={() => setIsEditing(false)}>
                        Cancelar
                      </Button>
                    </>
                  )}
                  {!isEditing && (
                    <Button
                      variant="outline"
                      onClick={shareDocument}
                      disabled={!draft}>
                      <Share2 className="h-4 w-4 mr-2" />
                      Compartir
                    </Button>
                  )}
                  {!isEditing && draft && (
                    <Button variant="outline" onClick={handlePrint}>
                      <Printer className="h-4 w-4 mr-2" />
                      Imprimir
                    </Button>
                  )}
                  {(!isEditing || draft) && (
                    <Button variant="outline" onClick={downloadDraft}>
                      <Download className="h-4 w-4 mr-2" />
                      Descargar
                    </Button>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                {isEditing ? (
                  <div>
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-sm font-medium text-gray-700">
                        Contenido del documento
                      </span>
                      <span className="text-xs text-gray-500">
                        {(editedDraft || "").length}/10000 caracteres
                      </span>
                    </div>
                    <Textarea
                      className="min-h-[300px] mb-4"
                      value={editedDraft || ""}
                      onChange={(e) => setEditedDraft(e.target.value)}
                      maxLength={10000}
                    />
                  </div>
                ) : (
                  <>
                    <style>{`
                      .doc-preview-content {
                        line-height: 1.8;
                        font-size: 1.1rem;
                      }
                      .doc-preview-content p {
                        margin-bottom: 1em;
                      }
                      .doc-preview-content br {
                        display: block;
                        margin-bottom: 0.5em;
                        content: "";
                      }
                    `}</style>
                    <div
                      className="prose doc-preview-content"
                      style={{
                        width: "100%",
                        fontSize: "1.1rem",
                        lineHeight: "1.7",
                      }}
                      dangerouslySetInnerHTML={{ __html: marked(draft) as string }}
                    />
                  </>
                )}
              </CardContent>
            </Card>
          ) : (
            <PreviewPlaceholder />
          )}
        </div>
      </TabsContent>
      <TabsContent value="history">
        <div className="mt-2">
          <h2 className="text-xl font-bold mb-4">
            Documentos generados recientemente
          </h2>
          <div className="space-y-6">
            {historyLoading ? (
              <div className="text-center py-8 text-gray-500">
                Cargando historial...
              </div>
            ) : documentsHistory.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                No hay documentos recientes.
              </div>
            ) : (
              documentsHistory.map((doc: DocumentHistory) => (
                <div
                  key={doc.id}
                  className="bg-white rounded-lg shadow p-6 flex flex-col gap-2 border">
                  <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2">
                    <div>
                      <div className="text-lg font-semibold">{doc.title}</div>
                      <div className="flex items-center gap-2 text-sm text-muted-foreground mt-1">
                        <Clock className="h-4 w-4" />
                        {format(new Date(doc.date), "dd/MMM/yyyy", {
                          locale: es,
                        })}
                        <span className="ml-2">{doc.type}</span>
                      </div>
                    </div>
                    <div className="flex gap-2 mt-2 md:mt-0">
                      <span
                        className={`px-3 py-1 rounded-full text-xs font-semibold ${doc.status === "Firmado"
                          ? "bg-blue-100 text-blue-700"
                          : doc.status === "Enviado"
                            ? "bg-gray-100 text-gray-700"
                            : "bg-gray-100 text-gray-700"
                          }`}>
                        {doc.status}
                      </span>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2 mt-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => download(doc)}>
                      <Download className="h-4 w-4 mr-1" /> Descargar
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => shareDocumentFromHistory(doc)}>
                      <Share2 className="h-4 w-4 mr-1" />
                      {doc.url ? "Compartir enlace" : "Compartir"}
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleViewDocument(doc)}>
                      <Eye className="h-4 w-4 mr-1" /> Ver
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleEditDocument(doc)}>
                      <Edit className="h-4 w-4 mr-1" /> Editar
                    </Button>
                    <Button
                      size="sm"
                      variant="destructive"
                      onClick={() => {
                        setPendingDelete(doc);
                        setShowDeleteModal(true);
                      }}>
                      <span className="mr-1">🗑️</span> Eliminar
                    </Button>
                  </div>
                </div>
              ))
            )}
          </div>
          {/* Confirmation Modal */}
          {showDeleteModal && pendingDelete && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-30">
              <div className="bg-white rounded-lg shadow-lg p-6 max-w-sm w-full">
                <h3 className="text-lg font-bold mb-2">
                  Confirmar eliminación
                </h3>
                <p className="mb-4">
                  ¿Está seguro de que desea eliminar el documento{" "}
                  <span className="font-semibold">{pendingDelete.title}</span>?
                </p>
                <div className="flex justify-end gap-2">
                  <Button
                    variant="outline"
                    onClick={() => {
                      setShowDeleteModal(false);
                      setPendingDelete(null);
                    }}>
                    Cancelar
                  </Button>
                  <Button
                    variant="destructive"
                    onClick={async () => {
                      await removeHistoryDoc(pendingDelete);
                      setShowDeleteModal(false);
                      setPendingDelete(null);
                    }}>
                    Eliminar
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>
      </TabsContent>

      {/* View Document Modal */}
      <ViewDocumentModal
        document={viewingDocument}
        isOpen={viewModalOpen}
        onClose={() => {
          setViewModalOpen(false);
          setViewingDocument(null);
        }}
        onEdit={handleEditDocument}
        onDownload={download}
        onShare={shareDocumentFromHistory}
      />
    </Tabs>
  );
}
